// SEO content for popular countries
// Keys are country names in French (as stored in the database)
import { newCountryContent } from "./newCountries";
import { additionalCountryContent } from "./additionalCountries";

export interface FAQItem {
  question: {
    fr: string;
    en: string;
    es: string;
  };
  answer: {
    fr: string;
    en: string;
    es: string;
  };
}

export interface TravelTip {
  title: {
    fr: string;
    en: string;
    es: string;
  };
  content: {
    fr: string;
    en: string;
    es: string;
  };
}

export interface CountryContent {
  slug: string;
  flag: string;
  description: {
    fr: string;
    en: string;
    es: string;
  };
  extendedDescription?: {
    fr: string;
    en: string;
    es: string;
  };
  keywords: {
    fr: string[];
    en: string[];
    es: string[];
  };
  highlights: string[];
  bestTimeToVisit: {
    fr: string;
    en: string;
    es: string;
  };
  relatedCountries: string[];
  faq?: FAQItem[];
  travelTips?: TravelTip[];
}

export const countryContent: Record<string, CountryContent> = {
  "Espagne": {
    slug: "espagne",
    flag: "🇪🇸",
    description: {
      fr: "L'Espagne vous invite à découvrir ses trésors : des plages dorées de la Costa Brava aux ruelles animées de Barcelone, en passant par l'Alhambra de Grenade et les tapas de Séville.",
      en: "Spain invites you to discover its treasures: from the golden beaches of Costa Brava to the lively streets of Barcelona, through Granada's Alhambra and Seville's tapas.",
      es: "España te invita a descubrir sus tesoros: desde las playas doradas de la Costa Brava hasta las animadas calles de Barcelona, pasando por la Alhambra de Granada y las tapas de Sevilla."
    },
    extendedDescription: {
      fr: "Avec son mélange unique de cultures, l'Espagne offre une expérience de voyage incomparable. Du flamenco andalou aux traditions basques, chaque région possède sa propre identité. Les amateurs d'art découvriront les œuvres de Gaudí, Picasso et Dalí, tandis que les gourmands se régaleront de paella, de jamón ibérico et de vins de La Rioja.",
      en: "With its unique blend of cultures, Spain offers an unparalleled travel experience. From Andalusian flamenco to Basque traditions, each region has its own identity. Art lovers will discover works by Gaudí, Picasso, and Dalí, while foodies will delight in paella, jamón ibérico, and Rioja wines.",
      es: "Con su mezcla única de culturas, España ofrece una experiencia de viaje incomparable. Desde el flamenco andaluz hasta las tradiciones vascas, cada región tiene su propia identidad."
    },
    keywords: {
      fr: ["voyage espagne", "roadtrip espagne", "itinéraire espagne", "vacances espagne"],
      en: ["spain trip", "spain roadtrip", "spain itinerary", "spain vacation"],
      es: ["viaje españa", "ruta españa", "itinerario españa", "vacaciones españa"]
    },
    highlights: ["Barcelone", "Madrid", "Séville", "Grenade", "Valence", "Îles Baléares"],
    bestTimeToVisit: {
      fr: "Avril à Juin et Septembre à Octobre",
      en: "April to June and September to October",
      es: "Abril a Junio y Septiembre a Octubre"
    },
    relatedCountries: ["Portugal", "France", "Italie", "Maroc"],
    faq: [
      {
        question: {
          fr: "Quelle est la meilleure période pour visiter l'Espagne ?",
          en: "What is the best time to visit Spain?",
          es: "¿Cuál es la mejor época para visitar España?"
        },
        answer: {
          fr: "Le printemps (avril-juin) et l'automne (septembre-octobre) sont idéaux. Les températures sont agréables et les foules moins nombreuses qu'en été.",
          en: "Spring (April-June) and autumn (September-October) are ideal. Temperatures are pleasant and crowds smaller than in summer.",
          es: "La primavera (abril-junio) y el otoño (septiembre-octubre) son ideales. Las temperaturas son agradables y hay menos turistas."
        }
      },
      {
        question: {
          fr: "Quel budget prévoir pour 2 semaines en Espagne ?",
          en: "What budget should I plan for 2 weeks in Spain?",
          es: "¿Qué presupuesto debo planificar para 2 semanas en España?"
        },
        answer: {
          fr: "Comptez entre 1500€ et 2500€ par personne pour 2 semaines, incluant hébergement, repas, transports et activités.",
          en: "Budget between €1500 and €2500 per person for 2 weeks, including accommodation, meals, transport, and activities.",
          es: "Cuenta entre 1500€ y 2500€ por persona para 2 semanas, incluyendo alojamiento, comidas, transporte y actividades."
        }
      },
      {
        question: {
          fr: "Comment se déplacer en Espagne ?",
          en: "How to get around in Spain?",
          es: "¿Cómo moverse por España?"
        },
        answer: {
          fr: "Le train AVE relie rapidement les grandes villes (Madrid-Barcelone en 2h30). La location de voiture est idéale pour explorer les régions rurales.",
          en: "The AVE train quickly connects major cities (Madrid-Barcelona in 2h30). Car rental is ideal for exploring rural areas.",
          es: "El tren AVE conecta rápidamente las grandes ciudades (Madrid-Barcelona en 2h30). El alquiler de coche es ideal para zonas rurales."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Horaires espagnols", en: "Spanish schedules", es: "Horarios españoles" },
        content: {
          fr: "Les Espagnols déjeunent vers 14h et dînent après 21h. Adaptez-vous à ce rythme pour profiter pleinement de la vie locale.",
          en: "Spaniards have lunch around 2pm and dinner after 9pm. Adapt to this rhythm to fully enjoy local life.",
          es: "Los españoles almuerzan hacia las 14h y cenan después de las 21h."
        }
      }
    ]
  },
  "France": {
    slug: "france",
    flag: "🇫🇷",
    description: {
      fr: "La France offre une diversité incomparable : des lavandes de Provence aux falaises d'Étretat, des châteaux de la Loire aux sommets alpins. Paris, la ville lumière, côtoie des villages pittoresques.",
      en: "France offers incomparable diversity: from the lavender fields of Provence to the cliffs of Étretat, from Loire castles to Alpine peaks.",
      es: "Francia ofrece una diversidad incomparable: desde los campos de lavanda de Provenza hasta los acantilados de Étretat."
    },
    extendedDescription: {
      fr: "La France est une destination incontournable qui séduit par sa diversité. Des vignobles de Bordeaux aux stations de ski des Alpes, des plages de la Côte d'Azur aux marais salants de Guérande, chaque voyage révèle de nouvelles merveilles.",
      en: "France is an essential destination that seduces with its diversity. From Bordeaux vineyards to Alpine ski resorts, from Côte d'Azur beaches to Guérande salt marshes.",
      es: "Francia es un destino imprescindible que seduce por su diversidad. Desde los viñedos de Burdeos hasta las estaciones de esquí de los Alpes."
    },
    keywords: {
      fr: ["voyage france", "roadtrip france", "itinéraire france", "vacances france"],
      en: ["france trip", "france roadtrip", "france itinerary", "france vacation"],
      es: ["viaje francia", "ruta francia", "itinerario francia", "vacaciones francia"]
    },
    highlights: ["Paris", "Nice", "Lyon", "Bordeaux", "Provence", "Mont Saint-Michel"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Espagne", "Italie", "Belgique", "Suisse"],
    faq: [
      {
        question: {
          fr: "Combien de jours prévoir pour visiter Paris ?",
          en: "How many days should I plan to visit Paris?",
          es: "¿Cuántos días debo planificar para visitar París?"
        },
        answer: {
          fr: "Prévoyez minimum 4-5 jours pour découvrir les incontournables : Tour Eiffel, Louvre, Montmartre, Notre-Dame. Pour une visite incluant Versailles, comptez 7 jours.",
          en: "Plan at least 4-5 days to discover the essentials: Eiffel Tower, Louvre, Montmartre, Notre-Dame. For a visit including Versailles, allow 7 days.",
          es: "Planifica mínimo 4-5 días para lo imprescindible: Torre Eiffel, Louvre, Montmartre, Notre-Dame. Para incluir Versalles, cuenta 7 días."
        }
      },
      {
        question: {
          fr: "La France est-elle chère pour les touristes ?",
          en: "Is France expensive for tourists?",
          es: "¿Es Francia cara para los turistas?"
        },
        answer: {
          fr: "Paris est cher, mais le reste de la France reste accessible. Comptez 80-120€/jour en moyenne. Les marchés locaux permettent d'économiser.",
          en: "Paris is expensive, but the rest of France remains accessible. Expect €80-120/day on average. Local markets help save on meals.",
          es: "París es caro, pero el resto de Francia sigue siendo accesible. Cuenta 80-120€/día en promedio."
        }
      }
    ]
  },
  "Italie": {
    slug: "italie",
    flag: "🇮🇹",
    description: {
      fr: "L'Italie enchante par son patrimoine millénaire : Rome et ses ruines antiques, Florence berceau de la Renaissance, Venise et ses canaux romantiques.",
      en: "Italy enchants with its millennia-old heritage: Rome and its ancient ruins, Florence the cradle of the Renaissance, Venice and its romantic canals.",
      es: "Italia encanta con su patrimonio milenario: Roma y sus ruinas antiguas, Florencia cuna del Renacimiento, Venecia y sus canales románticos."
    },
    extendedDescription: {
      fr: "L'Italie est un musée à ciel ouvert où chaque ville raconte une histoire. Des arènes romaines aux fresques de Michel-Ange, des Dolomites aux plages de Sardaigne, le pays offre une richesse culturelle et naturelle sans égale.",
      en: "Italy is an open-air museum where every city tells a story. From Roman arenas to Michelangelo's frescoes, from the Dolomites to Sardinian beaches.",
      es: "Italia es un museo al aire libre donde cada ciudad cuenta una historia. Desde los anfiteatros romanos hasta los frescos de Miguel Ángel."
    },
    keywords: {
      fr: ["voyage italie", "roadtrip italie", "itinéraire italie", "vacances italie"],
      en: ["italy trip", "italy roadtrip", "italy itinerary", "italy vacation"],
      es: ["viaje italia", "ruta italia", "itinerario italia", "vacaciones italia"]
    },
    highlights: ["Rome", "Florence", "Venise", "Milan", "Côte Amalfitaine", "Cinque Terre"],
    bestTimeToVisit: {
      fr: "Avril à Juin et Septembre à Octobre",
      en: "April to June and September to October",
      es: "Abril a Junio y Septiembre a Octubre"
    },
    relatedCountries: ["France", "Grèce", "Croatie", "Suisse"],
    faq: [
      {
        question: {
          fr: "Quel itinéraire pour 2 semaines en Italie ?",
          en: "What itinerary for 2 weeks in Italy?",
          es: "¿Qué itinerario para 2 semanas en Italia?"
        },
        answer: {
          fr: "Un classique : Rome (4j) → Florence (3j) → Cinque Terre (2j) → Venise (3j). Alternative : ajoutez la côte amalfitaine depuis Rome.",
          en: "A classic: Rome (4d) → Florence (3d) → Cinque Terre (2d) → Venice (3d). Alternative: add the Amalfi Coast from Rome.",
          es: "Un clásico: Roma (4d) → Florencia (3d) → Cinque Terre (2d) → Venecia (3d). Alternativa: añade la costa de Amalfi."
        }
      },
      {
        question: {
          fr: "Faut-il réserver les musées à l'avance ?",
          en: "Should I book museums in advance?",
          es: "¿Debo reservar los museos con antelación?"
        },
        answer: {
          fr: "Oui ! Le Vatican, les Offices à Florence et le Colisée nécessitent une réservation. Réservez 2-3 semaines avant pour éviter les files.",
          en: "Yes! The Vatican, Uffizi in Florence, and Colosseum require booking. Book 2-3 weeks ahead to avoid queues.",
          es: "¡Sí! El Vaticano, los Uffizi y el Coliseo requieren reserva. Reserva 2-3 semanas antes para evitar colas."
        }
      },
      {
        question: {
          fr: "Comment éviter les pièges à touristes en Italie ?",
          en: "How to avoid tourist traps in Italy?",
          es: "¿Cómo evitar las trampas para turistas en Italia?"
        },
        answer: {
          fr: "Évitez les restaurants autour des monuments. Cherchez les trattorias fréquentées par les locaux et méfiez-vous des menus traduits en plusieurs langues.",
          en: "Avoid restaurants around main monuments. Look for trattorias frequented by locals and beware of menus translated into multiple languages.",
          es: "Evita los restaurantes cerca de los monumentos. Busca trattorias frecuentadas por locales."
        }
      }
    ]
  },
  "Japon": {
    slug: "japon",
    flag: "🇯🇵",
    description: {
      fr: "Le Japon fascine par ses contrastes : Tokyo hypermoderne côtoie les temples millénaires de Kyoto, les cerisiers en fleurs ornent le mont Fuji.",
      en: "Japan fascinates with its contrasts: hypermodern Tokyo sits alongside Kyoto's millenary temples, cherry blossoms adorn Mount Fuji.",
      es: "Japón fascina por sus contrastes: Tokio hipermoderno convive con los templos milenarios de Kioto."
    },
    extendedDescription: {
      fr: "Le Japon est une destination qui bouleverse les sens. Des néons de Shibuya aux bambouseraies d'Arashiyama, des onsens traditionnels aux trains à grande vitesse, chaque instant est une découverte.",
      en: "Japan is a destination that shakes up the senses. From Shibuya's neon lights to Arashiyama's bamboo groves, from traditional onsens to high-speed trains.",
      es: "Japón es un destino que revoluciona los sentidos. Desde los neones de Shibuya hasta los bosques de bambú de Arashiyama."
    },
    keywords: {
      fr: ["voyage japon", "roadtrip japon", "itinéraire japon", "vacances japon"],
      en: ["japan trip", "japan roadtrip", "japan itinerary", "japan vacation"],
      es: ["viaje japón", "ruta japón", "itinerario japón", "vacaciones japón"]
    },
    highlights: ["Tokyo", "Kyoto", "Osaka", "Mont Fuji", "Nara", "Hiroshima"],
    bestTimeToVisit: {
      fr: "Mars à Mai et Octobre à Novembre",
      en: "March to May and October to November",
      es: "Marzo a Mayo y Octubre a Noviembre"
    },
    relatedCountries: ["Corée du Sud", "Thaïlande", "Vietnam", "Chine"],
    faq: [
      {
        question: {
          fr: "Quand voir les cerisiers en fleurs au Japon ?",
          en: "When to see cherry blossoms in Japan?",
          es: "¿Cuándo ver los cerezos en flor en Japón?"
        },
        answer: {
          fr: "La floraison varie selon les régions : fin mars à Tokyo, début avril à Kyoto. Réservez 3-6 mois à l'avance car c'est la haute saison.",
          en: "Blooming varies by region: late March in Tokyo, early April in Kyoto. Book accommodation 3-6 months ahead as it's peak season.",
          es: "La floración varía: finales de marzo en Tokio, principios de abril en Kioto. Reserva con 3-6 meses de antelación."
        }
      },
      {
        question: {
          fr: "Le JR Pass est-il rentable ?",
          en: "Is the JR Pass worth it?",
          es: "¿Merece la pena el JR Pass?"
        },
        answer: {
          fr: "Oui, si vous voyagez entre plusieurs villes. Un aller-retour Tokyo-Kyoto coûte presque le prix d'un JR Pass 7 jours.",
          en: "Yes, if traveling between multiple cities. A Tokyo-Kyoto round trip costs almost the price of a 7-day JR Pass.",
          es: "Sí, si viajas entre varias ciudades. Un viaje Tokio-Kioto ida y vuelta cuesta casi el precio de un JR Pass de 7 días."
        }
      },
      {
        question: {
          fr: "Comment communiquer au Japon sans parler japonais ?",
          en: "How to communicate in Japan without speaking Japanese?",
          es: "¿Cómo comunicarse en Japón sin hablar japonés?"
        },
        answer: {
          fr: "Utilisez Google Translate avec la caméra pour traduire les menus. Les applications Suica/Pasmo et Google Maps en anglais facilitent les déplacements.",
          en: "Use Google Translate with camera to translate menus and signs. Suica/Pasmo apps and Google Maps in English facilitate travel.",
          es: "Usa Google Translate con la cámara para traducir menús. Las apps Suica/Pasmo y Google Maps en inglés facilitan los desplazamientos."
        }
      }
    ]
  },
  "Islande": {
    slug: "islande",
    flag: "🇮🇸",
    description: {
      fr: "L'Islande offre des paysages d'un autre monde : geysers bouillonnants, glaciers majestueux, aurores boréales magiques.",
      en: "Iceland offers otherworldly landscapes: bubbling geysers, majestic glaciers, magical northern lights.",
      es: "Islandia ofrece paisajes de otro mundo: géiseres burbujeantes, glaciares majestuosos, auroras boreales mágicas."
    },
    extendedDescription: {
      fr: "L'Islande est une terre de superlatifs où la nature règne en maître. Cascades monumentales, plages de sable noir, volcans actifs et sources chaudes naturelles composent un tableau unique.",
      en: "Iceland is a land of superlatives where nature reigns supreme. Monumental waterfalls, black sand beaches, active volcanoes, and natural hot springs create a unique tableau.",
      es: "Islandia es una tierra de superlativos donde la naturaleza reina suprema. Cascadas monumentales, playas de arena negra, volcanes activos."
    },
    keywords: {
      fr: ["voyage islande", "roadtrip islande", "aurores boréales islande", "cercle d'or"],
      en: ["iceland trip", "iceland roadtrip", "northern lights iceland", "golden circle"],
      es: ["viaje islandia", "ruta islandia", "auroras boreales islandia", "círculo dorado"]
    },
    highlights: ["Reykjavik", "Cercle d'Or", "Blue Lagoon", "Vik", "Jökulsárlón", "Akureyri"],
    bestTimeToVisit: {
      fr: "Juin à Août (été) ou Septembre à Mars (aurores boréales)",
      en: "June to August (summer) or September to March (northern lights)",
      es: "Junio a Agosto (verano) o Septiembre a Marzo (auroras boreales)"
    },
    relatedCountries: ["Norvège", "Finlande", "Suède", "Danemark"],
    faq: [
      {
        question: {
          fr: "Combien coûte un voyage en Islande ?",
          en: "How much does a trip to Iceland cost?",
          es: "¿Cuánto cuesta un viaje a Islandia?"
        },
        answer: {
          fr: "L'Islande est chère : comptez 150-250€/jour/personne minimum. La location de 4x4 coûte 80-150€/jour. Économisez en campant et cuisinant.",
          en: "Iceland is expensive: expect €150-250/day/person minimum. 4x4 car rental costs €80-150/day. Save by camping and cooking.",
          es: "Islandia es cara: cuenta 150-250€/día/persona mínimo. El alquiler de 4x4 cuesta 80-150€/día."
        }
      },
      {
        question: {
          fr: "Peut-on voir des aurores boréales en Islande ?",
          en: "Can you see northern lights in Iceland?",
          es: "¿Se pueden ver auroras boreales en Islandia?"
        },
        answer: {
          fr: "Oui, de septembre à mars, loin des lumières de la ville et par ciel dégagé. Consultez les prévisions sur vedur.is.",
          en: "Yes, from September to March, away from city lights and under clear skies. Check aurora forecasts on vedur.is.",
          es: "Sí, de septiembre a marzo, lejos de las luces de la ciudad y con cielo despejado. Consulta las previsiones en vedur.is."
        }
      },
      {
        question: {
          fr: "Faut-il un 4x4 pour l'Islande ?",
          en: "Do I need a 4x4 for Iceland?",
          es: "¿Necesito un 4x4 para Islandia?"
        },
        answer: {
          fr: "En été, une voiture classique suffit pour la Ring Road. Un 4x4 est obligatoire pour les F-roads et recommandé en hiver.",
          en: "In summer, a regular car is enough for the Ring Road. A 4x4 is mandatory for F-roads and recommended in winter.",
          es: "En verano, un coche normal basta para la Ring Road. Un 4x4 es obligatorio para las F-roads y recomendado en invierno."
        }
      }
    ]
  },
  "Grèce": {
    slug: "grece",
    flag: "🇬🇷",
    description: {
      fr: "La Grèce vous transporte au cœur de la mythologie : l'Acropole d'Athènes, les couchers de soleil de Santorin, les plages turquoise des Cyclades.",
      en: "Greece transports you to the heart of mythology: Athens' Acropolis, Santorini's sunsets, the turquoise beaches of the Cyclades.",
      es: "Grecia te transporta al corazón de la mitología: la Acrópolis de Atenas, los atardeceres de Santorini."
    },
    extendedDescription: {
      fr: "La Grèce est le berceau de la civilisation occidentale, où chaque pierre raconte une histoire millénaire. Des ruines antiques de Delphes aux monastères suspendus des Météores, des villages blancs des Cyclades aux plages secrètes de Crète, le pays offre un mélange parfait d'histoire, de culture et de farniente méditerranéen.",
      en: "Greece is the cradle of Western civilization, where every stone tells a millennia-old story. From ancient ruins of Delphi to the suspended monasteries of Meteora, from white Cycladic villages to secret Cretan beaches, the country offers a perfect blend of history, culture, and Mediterranean relaxation.",
      es: "Grecia es la cuna de la civilización occidental, donde cada piedra cuenta una historia milenaria. Desde las ruinas de Delfos hasta los monasterios de Meteora, ofrece una mezcla perfecta de historia, cultura y relax mediterráneo."
    },
    keywords: {
      fr: ["voyage grèce", "roadtrip grèce", "itinéraire grèce", "îles grecques"],
      en: ["greece trip", "greece roadtrip", "greece itinerary", "greek islands"],
      es: ["viaje grecia", "ruta grecia", "itinerario grecia", "islas griegas"]
    },
    highlights: ["Athènes", "Santorin", "Mykonos", "Crète", "Rhodes", "Météores"],
    bestTimeToVisit: {
      fr: "Mai à Octobre",
      en: "May to October",
      es: "Mayo a Octubre"
    },
    relatedCountries: ["Turquie", "Italie", "Croatie", "Chypre"],
    faq: [
      {
        question: {
          fr: "Quelles îles grecques choisir pour un premier voyage ?",
          en: "Which Greek islands to choose for a first trip?",
          es: "¿Qué islas griegas elegir para un primer viaje?"
        },
        answer: {
          fr: "Santorin pour les paysages iconiques, Mykonos pour la vie nocturne, la Crète pour la diversité, et Naxos pour l'authenticité à prix doux.",
          en: "Santorini for iconic landscapes, Mykonos for nightlife, Crete for diversity, and Naxos for authenticity at affordable prices.",
          es: "Santorini para paisajes icónicos, Mykonos para vida nocturna, Creta para diversidad, y Naxos para autenticidad a precios asequibles."
        }
      },
      {
        question: {
          fr: "Comment se déplacer entre les îles grecques ?",
          en: "How to get between Greek islands?",
          es: "¿Cómo moverse entre las islas griegas?"
        },
        answer: {
          fr: "Les ferries sont le moyen principal : Blue Star et SeaJets relient les Cyclades. Réservez sur ferryhopper.com. En haute saison, réservez 1-2 semaines avant.",
          en: "Ferries are the main way: Blue Star and SeaJets connect the Cyclades. Book on ferryhopper.com. In high season, book 1-2 weeks ahead.",
          es: "Los ferries son el medio principal: Blue Star y SeaJets conectan las Cícladas. Reserva en ferryhopper.com."
        }
      },
      {
        question: {
          fr: "Quel budget pour 2 semaines en Grèce ?",
          en: "What budget for 2 weeks in Greece?",
          es: "¿Qué presupuesto para 2 semanas en Grecia?"
        },
        answer: {
          fr: "Comptez 1200-2000€ par personne hors vol. Santorin et Mykonos sont chers (150-200€/nuit). Les îles moins touristiques comme Naxos ou Paros sont plus abordables.",
          en: "Expect €1200-2000 per person excluding flights. Santorini and Mykonos are expensive (€150-200/night). Less touristy islands like Naxos or Paros are more affordable.",
          es: "Cuenta 1200-2000€ por persona sin vuelo. Santorini y Mykonos son caros. Islas menos turísticas como Naxos o Paros son más asequibles."
        }
      },
      {
        question: {
          fr: "Faut-il visiter Athènes ?",
          en: "Should I visit Athens?",
          es: "¿Debo visitar Atenas?"
        },
        answer: {
          fr: "Oui, 2-3 jours suffisent pour l'Acropole, Plaka, le musée archéologique et l'ambiance des tavernas. C'est aussi le hub pour les ferries vers les îles.",
          en: "Yes, 2-3 days are enough for the Acropolis, Plaka, the archaeological museum, and taverna atmosphere. It's also the ferry hub to the islands.",
          es: "Sí, 2-3 días bastan para la Acrópolis, Plaka, el museo arqueológico. También es el hub de ferries hacia las islas."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Réservez tôt pour Santorin", en: "Book early for Santorini", es: "Reserva pronto para Santorini" },
        content: {
          fr: "En juillet-août, les hôtels avec vue sur la caldeira affichent complet 3-4 mois à l'avance. Pour les meilleurs prix, voyagez en mai-juin ou septembre.",
          en: "In July-August, hotels with caldera views are fully booked 3-4 months ahead. For best prices, travel in May-June or September.",
          es: "En julio-agosto, los hoteles con vista a la caldera se agotan 3-4 meses antes. Para mejores precios, viaja en mayo-junio o septiembre."
        }
      },
      {
        title: { fr: "La sieste grecque", en: "Greek siesta", es: "La siesta griega" },
        content: {
          fr: "Comme en Espagne, beaucoup de commerces ferment entre 14h et 17h. Profitez-en pour vous reposer et ressortir en fin d'après-midi quand la chaleur diminue.",
          en: "Like in Spain, many shops close between 2pm and 5pm. Take the opportunity to rest and go out again in late afternoon when it cools down.",
          es: "Como en España, muchos comercios cierran entre las 14h y las 17h. Aprovecha para descansar."
        }
      }
    ]
  },
  "Portugal": {
    slug: "portugal",
    flag: "🇵🇹",
    description: {
      fr: "Le Portugal séduit par son authenticité : Lisbonne aux ruelles pavées et tramways vintage, Porto et ses caves à vin, l'Algarve et ses falaises spectaculaires.",
      en: "Portugal seduces with its authenticity: Lisbon with its cobbled streets and vintage trams, Porto and its wine cellars, the Algarve and its spectacular cliffs.",
      es: "Portugal seduce por su autenticidad: Lisboa con sus calles empedradas y tranvías vintage, Oporto y sus bodegas."
    },
    extendedDescription: {
      fr: "Le Portugal est une pépite européenne qui allie histoire maritime glorieuse, gastronomie savoureuse et paysages variés. Des palais féeriques de Sintra aux vagues légendaires de Nazaré, des vignobles du Douro aux plages sauvages de l'Alentejo, chaque région révèle un charme unique à prix doux.",
      en: "Portugal is a European gem combining glorious maritime history, delicious gastronomy, and varied landscapes. From Sintra's fairy-tale palaces to Nazaré's legendary waves, from Douro vineyards to wild Alentejo beaches, each region reveals unique charm at affordable prices.",
      es: "Portugal es una joya europea que combina historia marítima gloriosa, gastronomía deliciosa y paisajes variados. Desde los palacios de Sintra hasta las olas de Nazaré."
    },
    keywords: {
      fr: ["voyage portugal", "roadtrip portugal", "itinéraire portugal", "vacances portugal"],
      en: ["portugal trip", "portugal roadtrip", "portugal itinerary", "portugal vacation"],
      es: ["viaje portugal", "ruta portugal", "itinerario portugal", "vacaciones portugal"]
    },
    highlights: ["Lisbonne", "Porto", "Sintra", "Algarve", "Madère", "Açores"],
    bestTimeToVisit: {
      fr: "Mars à Octobre",
      en: "March to October",
      es: "Marzo a Octubre"
    },
    relatedCountries: ["Espagne", "France", "Maroc", "Italie"],
    faq: [
      {
        question: {
          fr: "Combien de temps pour visiter le Portugal ?",
          en: "How long to visit Portugal?",
          es: "¿Cuánto tiempo para visitar Portugal?"
        },
        answer: {
          fr: "10 jours permettent de voir Lisbonne (3j), Sintra (1j), Porto (2-3j) et l'Algarve (3j). Pour les Açores ou Madère, comptez 2 semaines.",
          en: "10 days allow you to see Lisbon (3d), Sintra (1d), Porto (2-3d) and the Algarve (3d). For Azores or Madeira, plan 2 weeks.",
          es: "10 días permiten ver Lisboa (3d), Sintra (1d), Oporto (2-3d) y el Algarve (3d). Para Azores o Madeira, cuenta 2 semanas."
        }
      },
      {
        question: {
          fr: "Le Portugal est-il bon marché ?",
          en: "Is Portugal affordable?",
          es: "¿Es Portugal económico?"
        },
        answer: {
          fr: "Oui, c'est l'une des destinations les moins chères d'Europe de l'Ouest. Comptez 60-100€/jour. Les repas sont excellents pour 10-15€.",
          en: "Yes, it's one of the cheapest Western European destinations. Expect €60-100/day. Meals are excellent for €10-15.",
          es: "Sí, es uno de los destinos más económicos de Europa Occidental. Cuenta 60-100€/día."
        }
      },
      {
        question: {
          fr: "Faut-il louer une voiture au Portugal ?",
          en: "Should I rent a car in Portugal?",
          es: "¿Debo alquilar un coche en Portugal?"
        },
        answer: {
          fr: "Pour Lisbonne et Porto, non. Pour l'Algarve, la vallée du Douro ou l'Alentejo, oui. Les autoroutes sont payantes (via-verde recommandé).",
          en: "For Lisbon and Porto, no. For the Algarve, Douro Valley, or Alentejo, yes. Highways are toll roads (via-verde recommended).",
          es: "Para Lisboa y Oporto, no. Para el Algarve, valle del Duero o Alentejo, sí. Las autopistas son de peaje."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Les pastéis de nata", en: "Pastéis de nata", es: "Los pastéis de nata" },
        content: {
          fr: "Ces petits flans à la crème sont une institution. Les meilleurs se trouvent à Belém (Pastéis de Belém) mais chaque boulangerie a sa recette. À déguster tièdes !",
          en: "These small custard tarts are an institution. The best are in Belém (Pastéis de Belém) but every bakery has its recipe. Best enjoyed warm!",
          es: "Estos pequeños flanes de crema son una institución. Los mejores están en Belém pero cada panadería tiene su receta."
        }
      },
      {
        title: { fr: "Le fado à Lisbonne", en: "Fado in Lisbon", es: "El fado en Lisboa" },
        content: {
          fr: "Assistez à un spectacle de fado dans l'Alfama ou le Bairro Alto. Réservez une casa de fado pour l'expérience authentique avec dîner inclus.",
          en: "Attend a fado show in Alfama or Bairro Alto. Book a casa de fado for the authentic experience with dinner included.",
          es: "Asiste a un espectáculo de fado en Alfama o Bairro Alto. Reserva una casa de fado para la experiencia auténtica."
        }
      }
    ]
  },
  "Croatie": {
    slug: "croatie",
    flag: "🇭🇷",
    description: {
      fr: "La Croatie révèle des trésors insoupçonnés : Dubrovnik la perle de l'Adriatique, les cascades de Plitvice, les îles paradisiaques de Hvar et Korčula.",
      en: "Croatia reveals unexpected treasures: Dubrovnik the pearl of the Adriatic, Plitvice waterfalls, the paradise islands of Hvar and Korčula.",
      es: "Croacia revela tesoros inesperados: Dubrovnik la perla del Adriático, las cascadas de Plitvice."
    },
    extendedDescription: {
      fr: "Entre mer Adriatique et montagnes, la Croatie offre une diversité remarquable. Ses villes médiévales parfaitement préservées, ses parcs nationaux aux eaux cristallines et sa cuisine méditerranéenne en font une destination idéale pour les amateurs d'histoire et de nature.",
      en: "Between the Adriatic Sea and mountains, Croatia offers remarkable diversity. Its perfectly preserved medieval towns, national parks with crystal-clear waters, and Mediterranean cuisine make it ideal for history and nature lovers.",
      es: "Entre el mar Adriático y las montañas, Croacia ofrece una diversidad notable. Sus ciudades medievales perfectamente conservadas y sus parques nacionales la convierten en un destino ideal."
    },
    keywords: {
      fr: ["voyage croatie", "roadtrip croatie", "itinéraire croatie", "vacances croatie"],
      en: ["croatia trip", "croatia roadtrip", "croatia itinerary", "croatia vacation"],
      es: ["viaje croacia", "ruta croacia", "itinerario croacia", "vacaciones croacia"]
    },
    highlights: ["Dubrovnik", "Split", "Plitvice", "Hvar", "Zagreb", "Zadar"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Slovénie", "Monténégro", "Italie", "Grèce"],
    faq: [
      {
        question: {
          fr: "Quel itinéraire pour une semaine en Croatie ?",
          en: "What itinerary for one week in Croatia?",
          es: "¿Qué itinerario para una semana en Croacia?"
        },
        answer: {
          fr: "Un classique : Dubrovnik (2j) → Split (2j) → Plitvice (1j) → Zagreb (2j). En été, ajoutez les îles de Hvar ou Korčula.",
          en: "A classic: Dubrovnik (2d) → Split (2d) → Plitvice (1d) → Zagreb (2d). In summer, add Hvar or Korčula islands.",
          es: "Un clásico: Dubrovnik (2d) → Split (2d) → Plitvice (1d) → Zagreb (2d). En verano, añade las islas de Hvar o Korčula."
        }
      },
      {
        question: {
          fr: "Faut-il réserver Plitvice à l'avance ?",
          en: "Should I book Plitvice in advance?",
          es: "¿Debo reservar Plitvice con antelación?"
        },
        answer: {
          fr: "Oui, obligatoire en été ! Les billets sont limités et se vendent vite. Réservez 1-2 semaines avant sur le site officiel.",
          en: "Yes, mandatory in summer! Tickets are limited and sell out fast. Book 1-2 weeks ahead on the official website.",
          es: "¡Sí, obligatorio en verano! Las entradas son limitadas y se agotan rápido. Reserva 1-2 semanas antes."
        }
      },
      {
        question: {
          fr: "La Croatie est-elle chère ?",
          en: "Is Croatia expensive?",
          es: "¿Es Croacia cara?"
        },
        answer: {
          fr: "Dubrovnik et Hvar sont chers (comparable à l'Europe de l'Ouest). Split et Zagreb restent plus abordables. Comptez 80-120€/jour.",
          en: "Dubrovnik and Hvar are expensive (comparable to Western Europe). Split and Zagreb remain more affordable. Expect €80-120/day.",
          es: "Dubrovnik y Hvar son caros (comparable a Europa Occidental). Split y Zagreb son más asequibles. Cuenta 80-120€/día."
        }
      }
    ]
  },
  "Maroc": {
    slug: "maroc",
    flag: "🇲🇦",
    description: {
      fr: "Le Maroc envoûte par ses couleurs et parfums : les souks de Marrakech, les ruelles bleues de Chefchaouen, les dunes dorées du Sahara.",
      en: "Morocco captivates with its colors and scents: Marrakech's souks, Chefchaouen's blue streets, the golden dunes of the Sahara.",
      es: "Marruecos cautiva por sus colores y aromas: los zocos de Marrakech, las calles azules de Chefchaouen."
    },
    extendedDescription: {
      fr: "Le Maroc est une porte vers l'Afrique et l'Orient, à seulement 2h de vol de l'Europe. Des médinas labyrinthiques aux palais somptueux, des oasis du sud aux montagnes de l'Atlas, le pays offre un dépaysement total. Sa cuisine raffinée (tagines, couscous, pastilla) et l'hospitalité légendaire des Marocains complètent l'expérience.",
      en: "Morocco is a gateway to Africa and the Orient, just 2 hours from Europe by plane. From labyrinthine medinas to sumptuous palaces, from southern oases to Atlas mountains, the country offers total immersion. Its refined cuisine (tagines, couscous, pastilla) and legendary Moroccan hospitality complete the experience.",
      es: "Marruecos es una puerta hacia África y Oriente, a solo 2 horas de Europa. Desde medinas laberínticas hasta palacios suntuosos, ofrece una inmersión total."
    },
    keywords: {
      fr: ["voyage maroc", "roadtrip maroc", "désert maroc", "marrakech"],
      en: ["morocco trip", "morocco roadtrip", "morocco desert", "marrakech"],
      es: ["viaje marruecos", "ruta marruecos", "desierto marruecos", "marrakech"]
    },
    highlights: ["Marrakech", "Fès", "Chefchaouen", "Désert du Sahara", "Essaouira", "Casablanca"],
    bestTimeToVisit: {
      fr: "Mars à Mai et Septembre à Novembre",
      en: "March to May and September to November",
      es: "Marzo a Mayo y Septiembre a Noviembre"
    },
    relatedCountries: ["Espagne", "Portugal", "Tunisie", "Égypte"],
    faq: [
      {
        question: {
          fr: "Le Maroc est-il sûr pour les touristes ?",
          en: "Is Morocco safe for tourists?",
          es: "¿Es Marruecos seguro para los turistas?"
        },
        answer: {
          fr: "Oui, le Maroc est globalement sûr. Les zones touristiques sont bien surveillées. Restez vigilant dans les médinas et négociez toujours les prix.",
          en: "Yes, Morocco is generally safe. Tourist areas are well-monitored. Stay vigilant in medinas and always negotiate prices.",
          es: "Sí, Marruecos es generalmente seguro. Las zonas turísticas están bien vigiladas. Mantente alerta en las medinas."
        }
      },
      {
        question: {
          fr: "Quel itinéraire pour 10 jours au Maroc ?",
          en: "What itinerary for 10 days in Morocco?",
          es: "¿Qué itinerario para 10 días en Marruecos?"
        },
        answer: {
          fr: "Marrakech (3j) → Ait Ben Haddou → Désert de Merzouga (2j) → Fès (2j) → Chefchaouen (2j). Retour par Tanger ou vol depuis Fès.",
          en: "Marrakech (3d) → Ait Ben Haddou → Merzouga Desert (2d) → Fes (2d) → Chefchaouen (2d). Return via Tangier or fly from Fes.",
          es: "Marrakech (3d) → Ait Ben Haddou → Desierto de Merzouga (2d) → Fez (2d) → Chefchaouen (2d)."
        }
      },
      {
        question: {
          fr: "Faut-il un visa pour le Maroc ?",
          en: "Do I need a visa for Morocco?",
          es: "¿Necesito visa para Marruecos?"
        },
        answer: {
          fr: "Non pour les Européens, Canadiens et Américains pour un séjour de moins de 90 jours. Passeport valide 6 mois après la date de retour.",
          en: "No for Europeans, Canadians, and Americans for stays under 90 days. Passport must be valid 6 months after return date.",
          es: "No para europeos, canadienses y estadounidenses para estancias menores de 90 días."
        }
      },
      {
        question: {
          fr: "Comment éviter les arnaques au Maroc ?",
          en: "How to avoid scams in Morocco?",
          es: "¿Cómo evitar las estafas en Marruecos?"
        },
        answer: {
          fr: "Négociez toujours (divisez le premier prix par 2-3), refusez poliment les guides non officiels, et convenez du prix avant de monter dans un taxi.",
          en: "Always negotiate (divide the first price by 2-3), politely refuse unofficial guides, and agree on the price before getting in a taxi.",
          es: "Negocia siempre (divide el primer precio por 2-3), rechaza educadamente a los guías no oficiales, y acuerda el precio antes de subir al taxi."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Dormez dans un riad", en: "Stay in a riad", es: "Duerme en un riad" },
        content: {
          fr: "Les riads sont des maisons traditionnelles avec patio central, transformées en maisons d'hôtes. Une expérience authentique et souvent plus charmante que les hôtels.",
          en: "Riads are traditional houses with a central patio, converted into guesthouses. An authentic experience often more charming than hotels.",
          es: "Los riads son casas tradicionales con patio central, convertidas en casas de huéspedes. Una experiencia auténtica."
        }
      },
      {
        title: { fr: "Le marchandage est culturel", en: "Bargaining is cultural", es: "El regateo es cultural" },
        content: {
          fr: "Dans les souks, le prix affiché n'est jamais le prix final. Marchandez avec le sourire, c'est un jeu social apprécié des commerçants !",
          en: "In the souks, the displayed price is never the final price. Bargain with a smile, it's a social game appreciated by merchants!",
          es: "En los zocos, el precio mostrado nunca es el final. ¡Regatea con una sonrisa, es un juego social!"
        }
      }
    ]
  },
  "Namibie": {
    slug: "namibie",
    flag: "🇳🇦",
    description: {
      fr: "La Namibie offre des paysages d'une beauté brute : les dunes rouges de Sossusvlei, le parc d'Etosha et sa faune exceptionnelle.",
      en: "Namibia offers landscapes of raw beauty: Sossusvlei's red dunes, Etosha Park and its exceptional wildlife.",
      es: "Namibia ofrece paisajes de belleza cruda: las dunas rojas de Sossusvlei, el parque Etosha."
    },
    extendedDescription: {
      fr: "La Namibie est un pays de contrastes saisissants. Des déserts les plus anciens du monde aux côtes battues par l'Atlantique, en passant par les savanes peuplées d'éléphants et de lions, chaque région offre une aventure unique.",
      en: "Namibia is a country of striking contrasts. From the world's oldest deserts to Atlantic-battered coasts, through savannas populated with elephants and lions, each region offers a unique adventure.",
      es: "Namibia es un país de contrastes llamativos. Desde los desiertos más antiguos del mundo hasta las costas azotadas por el Atlántico."
    },
    keywords: {
      fr: ["voyage namibie", "safari namibie", "désert namibie", "sossusvlei"],
      en: ["namibia trip", "namibia safari", "namibia desert", "sossusvlei"],
      es: ["viaje namibia", "safari namibia", "desierto namibia", "sossusvlei"]
    },
    highlights: ["Sossusvlei", "Etosha", "Skeleton Coast", "Fish River Canyon", "Swakopmund", "Windhoek"],
    bestTimeToVisit: {
      fr: "Mai à Octobre (saison sèche)",
      en: "May to October (dry season)",
      es: "Mayo a Octubre (estación seca)"
    },
    relatedCountries: ["Afrique du Sud", "Botswana", "Zimbabwe", "Zambie"],
    faq: [
      {
        question: {
          fr: "Combien de temps pour un roadtrip en Namibie ?",
          en: "How long for a Namibia road trip?",
          es: "¿Cuánto tiempo para un roadtrip en Namibia?"
        },
        answer: {
          fr: "Minimum 2 semaines pour les incontournables : Sossusvlei (3j), Swakopmund (2j), Etosha (3j), et les trajets. 3 semaines pour inclure le Damaraland et le Fish River Canyon.",
          en: "Minimum 2 weeks for the essentials: Sossusvlei (3d), Swakopmund (2d), Etosha (3d), plus driving. 3 weeks to include Damaraland and Fish River Canyon.",
          es: "Mínimo 2 semanas para lo esencial: Sossusvlei (3d), Swakopmund (2d), Etosha (3d), más conducción. 3 semanas para incluir Damaraland."
        }
      },
      {
        question: {
          fr: "Quel type de véhicule louer en Namibie ?",
          en: "What type of vehicle to rent in Namibia?",
          es: "¿Qué tipo de vehículo alquilar en Namibia?"
        },
        answer: {
          fr: "Un 4x4 avec tente de toit est idéal. Il permet d'accéder à tous les sites et de camper. Louez chez Asco Car Hire ou Namibia2Go.",
          en: "A 4x4 with rooftop tent is ideal. It provides access to all sites and camping. Rent from Asco Car Hire or Namibia2Go.",
          es: "Un 4x4 con tienda de techo es ideal. Permite acceder a todos los sitios y acampar."
        }
      },
      {
        question: {
          fr: "La Namibie est-elle sûre pour un roadtrip ?",
          en: "Is Namibia safe for a road trip?",
          es: "¿Es Namibia segura para un roadtrip?"
        },
        answer: {
          fr: "Oui, c'est l'un des pays les plus sûrs d'Afrique. Attention aux animaux sur les routes et à la fatigue (grandes distances). Ne roulez jamais de nuit.",
          en: "Yes, it's one of the safest African countries. Watch for animals on roads and fatigue (long distances). Never drive at night.",
          es: "Sí, es uno de los países más seguros de África. Cuidado con los animales en las carreteras y la fatiga."
        }
      }
    ]
  },
  "Thaïlande": {
    slug: "thailande",
    flag: "🇹🇭",
    description: {
      fr: "La Thaïlande enchante par sa diversité : temples dorés de Bangkok, plages paradisiaques de Phuket et Koh Samui, jungles du Nord autour de Chiang Mai.",
      en: "Thailand enchants with its diversity: Bangkok's golden temples, paradise beaches of Phuket and Koh Samui, northern jungles around Chiang Mai.",
      es: "Tailandia encanta por su diversidad: templos dorados de Bangkok, playas paradisíacas de Phuket y Koh Samui."
    },
    extendedDescription: {
      fr: "La Thaïlande est une destination complète qui séduit tous les voyageurs. Sa cuisine de rue légendaire, l'accueil chaleureux des Thaïlandais, ses îles paradisiaques et ses temples millénaires en font un incontournable de l'Asie du Sud-Est.",
      en: "Thailand is a complete destination that seduces all travelers. Its legendary street food, the warm welcome of Thai people, paradise islands, and millenary temples make it a Southeast Asia must-visit.",
      es: "Tailandia es un destino completo que seduce a todos los viajeros. Su legendaria comida callejera, la cálida bienvenida de los tailandeses y sus templos milenarios."
    },
    keywords: {
      fr: ["voyage thaïlande", "itinéraire thaïlande", "plages thaïlande", "temples bangkok"],
      en: ["thailand trip", "thailand itinerary", "thailand beaches", "bangkok temples"],
      es: ["viaje tailandia", "itinerario tailandia", "playas tailandia", "templos bangkok"]
    },
    highlights: ["Bangkok", "Phuket", "Chiang Mai", "Koh Samui", "Krabi", "Ayutthaya"],
    bestTimeToVisit: {
      fr: "Novembre à Février",
      en: "November to February",
      es: "Noviembre a Febrero"
    },
    relatedCountries: ["Vietnam", "Cambodge", "Laos", "Indonésie"],
    faq: [
      {
        question: {
          fr: "Quel itinéraire pour 3 semaines en Thaïlande ?",
          en: "What itinerary for 3 weeks in Thailand?",
          es: "¿Qué itinerario para 3 semanas en Tailandia?"
        },
        answer: {
          fr: "Bangkok (3j) → Chiang Mai (4j) → Pai (2j) → îles du Sud : Krabi/Koh Phi Phi (4j) → Koh Lanta ou Koh Samui (4j). Vol interne recommandé.",
          en: "Bangkok (3d) → Chiang Mai (4d) → Pai (2d) → Southern islands: Krabi/Koh Phi Phi (4d) → Koh Lanta or Koh Samui (4d). Domestic flight recommended.",
          es: "Bangkok (3d) → Chiang Mai (4d) → Pai (2d) → islas del Sur: Krabi/Koh Phi Phi (4d) → Koh Lanta o Koh Samui (4d)."
        }
      },
      {
        question: {
          fr: "Faut-il un visa pour la Thaïlande ?",
          en: "Do I need a visa for Thailand?",
          es: "¿Necesito visa para Tailandia?"
        },
        answer: {
          fr: "Non pour les séjours de moins de 45 jours (Français, Belges, Suisses). Au-delà, demandez un visa touriste de 60 jours.",
          en: "No for stays under 45 days (EU citizens). Beyond that, apply for a 60-day tourist visa.",
          es: "No para estancias menores de 45 días (ciudadanos de la UE). Para más tiempo, solicita un visado de turista de 60 días."
        }
      },
      {
        question: {
          fr: "Quelles îles thaïlandaises choisir ?",
          en: "Which Thai islands to choose?",
          es: "¿Qué islas tailandesas elegir?"
        },
        answer: {
          fr: "Koh Phi Phi et Krabi pour les paysages spectaculaires, Koh Lanta pour le calme, Koh Samui pour le confort, Koh Tao pour la plongée.",
          en: "Koh Phi Phi and Krabi for spectacular scenery, Koh Lanta for tranquility, Koh Samui for comfort, Koh Tao for diving.",
          es: "Koh Phi Phi y Krabi para paisajes espectaculares, Koh Lanta para tranquilidad, Koh Samui para confort, Koh Tao para buceo."
        }
      }
    ]
  },
  "États-Unis": {
    slug: "etats-unis",
    flag: "🇺🇸",
    description: {
      fr: "Les États-Unis offrent une diversité époustouflante : la démesure de New York, les parcs nationaux grandioses de l'Ouest, les plages de Californie et de Floride.",
      en: "The United States offers stunning diversity: New York's grandeur, the magnificent national parks of the West, California and Florida beaches.",
      es: "Estados Unidos ofrece una diversidad impresionante: la grandeza de Nueva York, los magníficos parques nacionales del Oeste."
    },
    extendedDescription: {
      fr: "Les États-Unis sont un continent à eux seuls. Des gratte-ciels de Manhattan aux canyons de l'Arizona, des plages hawaïennes aux forêts de l'Oregon, chaque État offre une expérience unique. Le roadtrip américain reste un rêve pour de nombreux voyageurs.",
      en: "The United States is a continent unto itself. From Manhattan's skyscrapers to Arizona's canyons, from Hawaiian beaches to Oregon's forests, each state offers a unique experience.",
      es: "Estados Unidos es un continente en sí mismo. Desde los rascacielos de Manhattan hasta los cañones de Arizona, cada estado ofrece una experiencia única."
    },
    keywords: {
      fr: ["voyage usa", "roadtrip états-unis", "parcs nationaux usa", "vacances amérique"],
      en: ["usa trip", "usa roadtrip", "national parks usa", "america vacation"],
      es: ["viaje usa", "ruta estados unidos", "parques nacionales usa", "vacaciones américa"]
    },
    highlights: ["New York", "Los Angeles", "San Francisco", "Grand Canyon", "Yellowstone", "Miami"],
    bestTimeToVisit: {
      fr: "Varie selon la région - Printemps et Automne idéaux",
      en: "Varies by region - Spring and Fall are ideal",
      es: "Varía según la región - Primavera y Otoño son ideales"
    },
    relatedCountries: ["Canada", "Mexique", "Cuba", "Bahamas"],
    faq: [
      {
        question: {
          fr: "Faut-il une ESTA ou un visa pour les USA ?",
          en: "Do I need an ESTA or visa for the USA?",
          es: "¿Necesito ESTA o visa para EE.UU.?"
        },
        answer: {
          fr: "L'ESTA suffit pour un séjour de moins de 90 jours (tourisme/affaires). Demandez-la au moins 72h avant le départ sur le site officiel (esta.cbp.dhs.gov).",
          en: "ESTA is enough for stays under 90 days (tourism/business). Apply at least 72h before departure on the official site (esta.cbp.dhs.gov).",
          es: "La ESTA es suficiente para estancias menores de 90 días. Solicítala al menos 72h antes en el sitio oficial (esta.cbp.dhs.gov)."
        }
      },
      {
        question: {
          fr: "Quel roadtrip pour découvrir l'Ouest américain ?",
          en: "What road trip to discover the American West?",
          es: "¿Qué roadtrip para descubrir el Oeste americano?"
        },
        answer: {
          fr: "Le classique en 2 semaines : Los Angeles → Las Vegas → Grand Canyon → Monument Valley → Antelope Canyon → Bryce → Zion → Las Vegas. Environ 2500 km.",
          en: "The classic in 2 weeks: Los Angeles → Las Vegas → Grand Canyon → Monument Valley → Antelope Canyon → Bryce → Zion → Las Vegas. About 2500 km.",
          es: "El clásico en 2 semanas: Los Ángeles → Las Vegas → Grand Canyon → Monument Valley → Antelope Canyon → Bryce → Zion → Las Vegas."
        }
      },
      {
        question: {
          fr: "Combien coûte un roadtrip aux États-Unis ?",
          en: "How much does a US road trip cost?",
          es: "¿Cuánto cuesta un roadtrip en Estados Unidos?"
        },
        answer: {
          fr: "Comptez 100-150€/jour/personne (hors vol). Location de voiture : 40-70€/jour. Hébergement : 80-150€/nuit. L'essence est bon marché.",
          en: "Expect €100-150/day/person (excluding flight). Car rental: €40-70/day. Accommodation: €80-150/night. Gas is cheap.",
          es: "Cuenta 100-150€/día/persona (sin vuelo). Alquiler de coche: 40-70€/día. Alojamiento: 80-150€/noche."
        }
      }
    ]
  },
  "Slovénie": {
    slug: "slovenie",
    flag: "🇸🇮",
    description: {
      fr: "La Slovénie surprend par sa diversité : le lac de Bled et son île romantique, les grottes de Postojna, les Alpes Juliennes.",
      en: "Slovenia surprises with its diversity: Lake Bled and its romantic island, Postojna caves, the Julian Alps.",
      es: "Eslovenia sorprende por su diversidad: el lago de Bled y su isla romántica, las cuevas de Postojna."
    },
    extendedDescription: {
      fr: "La Slovénie est un joyau méconnu au cœur de l'Europe. Ce petit pays concentre une diversité remarquable : lacs alpins aux eaux émeraude, grottes spectaculaires, vignobles vallonnés et côte adriatique pittoresque. Ljubljana, sa capitale verte et piétonne, est l'une des plus charmantes d'Europe.",
      en: "Slovenia is an underrated gem in the heart of Europe. This small country packs remarkable diversity: alpine lakes with emerald waters, spectacular caves, rolling vineyards, and a picturesque Adriatic coast. Ljubljana, its green, pedestrian capital, is one of Europe's most charming.",
      es: "Eslovenia es una joya desconocida en el corazón de Europa. Este pequeño país concentra una diversidad notable: lagos alpinos, cuevas espectaculares, viñedos y costa adriática pintoresca."
    },
    keywords: {
      fr: ["voyage slovénie", "lac de bled", "ljubljana", "grottes postojna"],
      en: ["slovenia trip", "lake bled", "ljubljana", "postojna caves"],
      es: ["viaje eslovenia", "lago de bled", "liubliana", "cuevas de postojna"]
    },
    highlights: ["Bled", "Ljubljana", "Postojna", "Piran", "Triglav", "Grottes de Škocjan"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Croatie", "Italie", "Autriche", "Hongrie"],
    faq: [
      {
        question: {
          fr: "Combien de jours pour visiter la Slovénie ?",
          en: "How many days to visit Slovenia?",
          es: "¿Cuántos días para visitar Eslovenia?"
        },
        answer: {
          fr: "5-7 jours suffisent pour les incontournables : Ljubljana (2j), Bled (1-2j), Postojna/Škocjan (1j), Piran et la côte (1j), vallée de la Soča (1j).",
          en: "5-7 days are enough for the essentials: Ljubljana (2d), Bled (1-2d), Postojna/Škocjan (1d), Piran and coast (1d), Soča Valley (1d).",
          es: "5-7 días bastan para lo esencial: Liubliana (2d), Bled (1-2d), Postojna/Škocjan (1d), Piran y costa (1d), valle del Soča (1d)."
        }
      },
      {
        question: {
          fr: "La Slovénie est-elle chère ?",
          en: "Is Slovenia expensive?",
          es: "¿Es Eslovenia cara?"
        },
        answer: {
          fr: "Moins chère que l'Autriche ou l'Italie voisines. Comptez 70-100€/jour. Les hébergements autour de Bled sont plus chers en haute saison.",
          en: "Cheaper than neighboring Austria or Italy. Expect €70-100/day. Accommodations around Bled are pricier in high season.",
          es: "Más barata que Austria o Italia vecinas. Cuenta 70-100€/día."
        }
      },
      {
        question: {
          fr: "Faut-il louer une voiture en Slovénie ?",
          en: "Should I rent a car in Slovenia?",
          es: "¿Debo alquilar un coche en Eslovenia?"
        },
        answer: {
          fr: "Oui, recommandé pour explorer librement. Le pays est petit (on traverse en 2h). N'oubliez pas la vignette autoroute (vinjeta) obligatoire.",
          en: "Yes, recommended to explore freely. The country is small (2h to cross). Don't forget the mandatory highway vignette (vinjeta).",
          es: "Sí, recomendado para explorar libremente. El país es pequeño. No olvides la viñeta de autopista (vinjeta) obligatoria."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "La kremšnita de Bled", en: "Bled's kremšnita", es: "La kremšnita de Bled" },
        content: {
          fr: "Ce gâteau à la crème vanille et pâte feuilletée est la spécialité locale. Dégustez-le au café du Park Hotel avec vue sur le lac !",
          en: "This vanilla cream and puff pastry cake is the local specialty. Enjoy it at the Park Hotel café with lake views!",
          es: "Este pastel de crema de vainilla y hojaldre es la especialidad local. ¡Degústalo en el café del Park Hotel con vistas al lago!"
        }
      },
      {
        title: { fr: "Randonnée accessible", en: "Accessible hiking", es: "Senderismo accesible" },
        content: {
          fr: "La Slovénie offre des sentiers pour tous niveaux. Le parc national du Triglav propose des randonnées spectaculaires avec refuges de montagne.",
          en: "Slovenia offers trails for all levels. Triglav National Park has spectacular hikes with mountain huts for overnight stays.",
          es: "Eslovenia ofrece senderos para todos los niveles. El parque nacional del Triglav tiene caminatas espectaculares con refugios de montaña."
        }
      }
    ]
  },
  "Canada": {
    slug: "canada",
    flag: "🇨🇦",
    description: {
      fr: "Le Canada impressionne par ses grands espaces : les Rocheuses majestueuses, les forêts infinies, les métropoles cosmopolites de Toronto, Montréal et Vancouver.",
      en: "Canada impresses with its vast spaces: majestic Rockies, endless forests, cosmopolitan metropolises of Toronto, Montreal, and Vancouver.",
      es: "Canadá impresiona por sus grandes espacios: las majestuosas Rocosas, bosques infinitos."
    },
    extendedDescription: {
      fr: "Le Canada est un pays aux dimensions démesurées qui fascine par sa nature préservée. Des lacs turquoise des Rocheuses aux aurores boréales du Yukon, des baleines de Tadoussac aux ours de Colombie-Britannique, l'aventure est partout.",
      en: "Canada is a country of immense dimensions that fascinates with its preserved nature. From the turquoise lakes of the Rockies to Yukon's northern lights, from Tadoussac whales to British Columbia bears, adventure is everywhere.",
      es: "Canadá es un país de dimensiones inmensas que fascina por su naturaleza preservada. Desde los lagos turquesa de las Rocosas hasta las auroras boreales del Yukón."
    },
    keywords: {
      fr: ["voyage canada", "roadtrip canada", "rocheuses canadiennes", "parcs nationaux canada"],
      en: ["canada trip", "canada roadtrip", "canadian rockies", "canada national parks"],
      es: ["viaje canadá", "ruta canadá", "rocosas canadienses", "parques nacionales canadá"]
    },
    highlights: ["Vancouver", "Toronto", "Montréal", "Banff", "Québec", "Chutes du Niagara"],
    bestTimeToVisit: {
      fr: "Juin à Septembre (été) ou Décembre à Mars (ski)",
      en: "June to September (summer) or December to March (skiing)",
      es: "Junio a Septiembre (verano) o Diciembre a Marzo (esquí)"
    },
    relatedCountries: ["États-Unis", "Islande", "Norvège", "Alaska"],
    faq: [
      {
        question: {
          fr: "Faut-il un visa pour le Canada ?",
          en: "Do I need a visa for Canada?",
          es: "¿Necesito visa para Canadá?"
        },
        answer: {
          fr: "Non, mais une AVE (Autorisation de Voyage Électronique) est obligatoire. Demandez-la en ligne (7$CAD) au moins 72h avant le départ.",
          en: "No, but an eTA (Electronic Travel Authorization) is mandatory. Apply online ($7CAD) at least 72h before departure.",
          es: "No, pero una eTA (Autorización de Viaje Electrónica) es obligatoria. Solicítala en línea ($7CAD) al menos 72h antes."
        }
      },
      {
        question: {
          fr: "Quel itinéraire pour les Rocheuses canadiennes ?",
          en: "What itinerary for the Canadian Rockies?",
          es: "¿Qué itinerario para las Rocosas canadienses?"
        },
        answer: {
          fr: "En 10 jours : Calgary → Banff (3j) → Lake Louise (2j) → Icefields Parkway → Jasper (3j) → retour Calgary. Une des plus belles routes du monde !",
          en: "In 10 days: Calgary → Banff (3d) → Lake Louise (2d) → Icefields Parkway → Jasper (3d) → back to Calgary. One of the world's most beautiful drives!",
          es: "En 10 días: Calgary → Banff (3d) → Lake Louise (2d) → Icefields Parkway → Jasper (3d) → vuelta a Calgary."
        }
      },
      {
        question: {
          fr: "Quand voir les aurores boréales au Canada ?",
          en: "When to see northern lights in Canada?",
          es: "¿Cuándo ver auroras boreales en Canadá?"
        },
        answer: {
          fr: "De septembre à mars, au Yukon, dans les Territoires du Nord-Ouest (Yellowknife) ou au nord du Québec. Les mois de mars et septembre sont optimaux.",
          en: "From September to March, in Yukon, Northwest Territories (Yellowknife), or northern Quebec. March and September are optimal months.",
          es: "De septiembre a marzo, en Yukón, Territorios del Noroeste (Yellowknife) o norte de Quebec."
        }
      }
    ]
  },
  "Mexique": {
    slug: "mexique",
    flag: "🇲🇽",
    description: {
      fr: "Le Mexique fascine par sa richesse culturelle : pyramides mayas du Yucatán, villes coloniales colorées, plages de la Riviera Maya.",
      en: "Mexico fascinates with its cultural richness: Mayan pyramids of Yucatan, colorful colonial towns, Riviera Maya beaches.",
      es: "México fascina por su riqueza cultural: pirámides mayas del Yucatán, ciudades coloniales coloridas."
    },
    extendedDescription: {
      fr: "Le Mexique est un pays aux mille facettes. Des ruines mystérieuses de Palenque aux cenotes cristallins du Yucatán, de la street food de Mexico City aux mariachis de Guadalajara, chaque région dévoile une culture vibrante.",
      en: "Mexico is a country of a thousand facets. From the mysterious ruins of Palenque to Yucatan's crystalline cenotes, from Mexico City's street food to Guadalajara's mariachis, each region unveils a vibrant culture.",
      es: "México es un país de mil facetas. Desde las ruinas misteriosas de Palenque hasta los cenotes cristalinos del Yucatán."
    },
    keywords: {
      fr: ["voyage mexique", "roadtrip mexique", "riviera maya", "yucatan"],
      en: ["mexico trip", "mexico roadtrip", "riviera maya", "yucatan"],
      es: ["viaje méxico", "ruta méxico", "riviera maya", "yucatán"]
    },
    highlights: ["Cancún", "Mexico City", "Playa del Carmen", "Oaxaca", "Tulum", "Chichén Itzá"],
    bestTimeToVisit: {
      fr: "Novembre à Avril",
      en: "November to April",
      es: "Noviembre a Abril"
    },
    relatedCountries: ["États-Unis", "Guatemala", "Cuba", "Costa Rica"],
    faq: [
      {
        question: {
          fr: "Le Mexique est-il dangereux pour les touristes ?",
          en: "Is Mexico dangerous for tourists?",
          es: "¿Es México peligroso para los turistas?"
        },
        answer: {
          fr: "Les zones touristiques (Yucatán, Oaxaca, Mexico City) sont sûres. Évitez certains États du nord et restez vigilant la nuit. Le tourisme est bien développé.",
          en: "Tourist areas (Yucatan, Oaxaca, Mexico City) are safe. Avoid certain northern states and stay vigilant at night. Tourism is well developed.",
          es: "Las zonas turísticas (Yucatán, Oaxaca, Ciudad de México) son seguras. Evita ciertos estados del norte."
        }
      },
      {
        question: {
          fr: "Quel itinéraire pour le Yucatán ?",
          en: "What itinerary for the Yucatan?",
          es: "¿Qué itinerario para el Yucatán?"
        },
        answer: {
          fr: "En 2 semaines : Cancún → Isla Holbox (2j) → Valladolid/Chichén Itzá (2j) → Mérida (2j) → Celestún → Campeche → Bacalar (3j) → Tulum (3j).",
          en: "In 2 weeks: Cancun → Isla Holbox (2d) → Valladolid/Chichen Itza (2d) → Merida (2d) → Celestun → Campeche → Bacalar (3d) → Tulum (3d).",
          es: "En 2 semanas: Cancún → Isla Holbox (2d) → Valladolid/Chichén Itzá (2d) → Mérida (2d) → Celestún → Campeche → Bacalar (3d) → Tulum (3d)."
        }
      },
      {
        question: {
          fr: "Qu'est-ce qu'un cenote ?",
          en: "What is a cenote?",
          es: "¿Qué es un cenote?"
        },
        answer: {
          fr: "Un cenote est une piscine naturelle formée dans la roche calcaire, typique du Yucatán. On peut s'y baigner ! Les plus beaux : Gran Cenote, Cenote Suytun, Ik Kil.",
          en: "A cenote is a natural pool formed in limestone, typical of the Yucatan. You can swim in them! Best ones: Gran Cenote, Cenote Suytun, Ik Kil.",
          es: "Un cenote es una piscina natural formada en roca caliza, típica del Yucatán. ¡Puedes nadar! Los mejores: Gran Cenote, Cenote Suytun, Ik Kil."
        }
      }
    ]
  },
  "Indonésie": {
    slug: "indonesie",
    flag: "🇮🇩",
    description: {
      fr: "L'Indonésie enchante par sa diversité : les rizières en terrasses de Bali, les temples de Java, les plages paradisiaques de Lombok et les fonds marins de Raja Ampat.",
      en: "Indonesia enchants with its diversity: Bali's terraced rice fields, Java's temples, Lombok's paradise beaches, and Raja Ampat's underwater wonders.",
      es: "Indonesia encanta por su diversidad: los arrozales de Bali, los templos de Java, las playas paradisíacas de Lombok."
    },
    extendedDescription: {
      fr: "L'Indonésie est un archipel de plus de 17 000 îles offrant une incroyable diversité. Des volcans fumants de Java aux orangs-outans de Sumatra, des cérémonies hindouistes de Bali aux villages traditionnels de Flores, chaque île révèle une culture et des paysages uniques.",
      en: "Indonesia is an archipelago of over 17,000 islands offering incredible diversity. From Java's smoking volcanoes to Sumatra's orangutans, from Bali's Hindu ceremonies to Flores' traditional villages, each island reveals unique culture and landscapes.",
      es: "Indonesia es un archipiélago de más de 17.000 islas que ofrece una diversidad increíble. Desde los volcanes de Java hasta los orangutanes de Sumatra."
    },
    keywords: {
      fr: ["voyage indonésie", "bali voyage", "itinéraire indonésie", "java bali"],
      en: ["indonesia trip", "bali travel", "indonesia itinerary", "java bali"],
      es: ["viaje indonesia", "bali viaje", "itinerario indonesia", "java bali"]
    },
    highlights: ["Bali", "Java", "Lombok", "Komodo", "Raja Ampat", "Sumatra"],
    bestTimeToVisit: {
      fr: "Avril à Octobre (saison sèche)",
      en: "April to October (dry season)",
      es: "Abril a Octubre (estación seca)"
    },
    relatedCountries: ["Thaïlande", "Vietnam", "Malaisie", "Philippines"],
    faq: [
      {
        question: {
          fr: "Combien de temps pour visiter l'Indonésie ?",
          en: "How long to visit Indonesia?",
          es: "¿Cuánto tiempo para visitar Indonesia?"
        },
        answer: {
          fr: "Minimum 2 semaines pour Bali + Lombok ou Java. 3-4 semaines pour combiner plusieurs îles. L'Indonésie est immense, ne sous-estimez pas les distances !",
          en: "Minimum 2 weeks for Bali + Lombok or Java. 3-4 weeks to combine multiple islands. Indonesia is huge, don't underestimate distances!",
          es: "Mínimo 2 semanas para Bali + Lombok o Java. 3-4 semanas para combinar varias islas."
        }
      },
      {
        question: {
          fr: "Bali est-elle surfréquentée ?",
          en: "Is Bali overcrowded?",
          es: "¿Está Bali masificada?"
        },
        answer: {
          fr: "Le sud (Kuta, Seminyak) est très touristique. Préférez Ubud, Sidemen, Munduk ou le nord pour plus d'authenticité. Les îles Gili et Nusa Penida sont des alternatives.",
          en: "The south (Kuta, Seminyak) is very touristy. Prefer Ubud, Sidemen, Munduk, or the north for more authenticity. Gili and Nusa Penida islands are alternatives.",
          es: "El sur (Kuta, Seminyak) es muy turístico. Prefiere Ubud, Sidemen, Munduk o el norte para más autenticidad."
        }
      },
      {
        question: {
          fr: "Faut-il un visa pour l'Indonésie ?",
          en: "Do I need a visa for Indonesia?",
          es: "¿Necesito visa para Indonesia?"
        },
        answer: {
          fr: "Oui, un visa à l'arrivée (VOA) de 30 jours est disponible pour 35$. Il est prolongeable une fois. Pour plus de 60 jours, demandez un visa B211A.",
          en: "Yes, a 30-day visa on arrival (VOA) is available for $35. It can be extended once. For more than 60 days, apply for a B211A visa.",
          es: "Sí, un visado a la llegada (VOA) de 30 días está disponible por 35$. Se puede extender una vez."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Louer un scooter", en: "Rent a scooter", es: "Alquilar una moto" },
        content: {
          fr: "Le scooter est le moyen de transport idéal à Bali. Comptez 50-70k IDR/jour. Ayez un permis international et portez toujours un casque.",
          en: "Scooters are the ideal transport in Bali. Expect 50-70k IDR/day. Have an international license and always wear a helmet.",
          es: "La moto es el transporte ideal en Bali. Cuenta 50-70k IDR/día. Ten un permiso internacional y usa siempre casco."
        }
      },
      {
        title: { fr: "Attention aux singes", en: "Watch out for monkeys", es: "Cuidado con los monos" },
        content: {
          fr: "Dans les temples comme Ubud Monkey Forest, sécurisez vos affaires (lunettes, téléphone). Les macaques sont malins et peuvent voler !",
          en: "At temples like Ubud Monkey Forest, secure your belongings (glasses, phone). Macaques are clever and can steal!",
          es: "En templos como Ubud Monkey Forest, asegura tus pertenencias. ¡Los macacos son listos y pueden robar!"
        }
      }
    ]
  },
  "Vietnam": {
    slug: "vietnam",
    flag: "🇻🇳",
    description: {
      fr: "Le Vietnam séduit par ses paysages époustouflants : la baie d'Halong et ses karsts, les rizières de Sapa, les lanternes de Hoi An et le delta du Mékong.",
      en: "Vietnam seduces with its stunning landscapes: Halong Bay and its karsts, Sapa's rice terraces, Hoi An's lanterns, and the Mekong Delta.",
      es: "Vietnam seduce por sus paisajes impresionantes: la bahía de Halong, los arrozales de Sapa, las linternas de Hoi An."
    },
    extendedDescription: {
      fr: "Le Vietnam est un pays tout en longueur qui se découvre du nord au sud. Des montagnes brumeuses du Tonkin aux plages tropicales du sud, en passant par la cuisine de rue légendaire et l'histoire poignante, chaque étape révèle une facette différente de ce pays fascinant.",
      en: "Vietnam is a long country discovered from north to south. From the misty mountains of Tonkin to the tropical beaches of the south, through legendary street food and poignant history, each stop reveals a different facet of this fascinating country.",
      es: "Vietnam es un país largo que se descubre de norte a sur. Desde las montañas brumosas del Tonkín hasta las playas tropicales del sur."
    },
    keywords: {
      fr: ["voyage vietnam", "baie halong", "itinéraire vietnam", "hanoi saigon"],
      en: ["vietnam trip", "halong bay", "vietnam itinerary", "hanoi saigon"],
      es: ["viaje vietnam", "bahía halong", "itinerario vietnam", "hanoi saigon"]
    },
    highlights: ["Hanoi", "Baie d'Halong", "Hoi An", "Ho Chi Minh", "Sapa", "Ninh Binh"],
    bestTimeToVisit: {
      fr: "Octobre à Avril (Nord) / Décembre à Avril (Sud)",
      en: "October to April (North) / December to April (South)",
      es: "Octubre a Abril (Norte) / Diciembre a Abril (Sur)"
    },
    relatedCountries: ["Cambodge", "Laos", "Thaïlande", "Chine"],
    faq: [
      {
        question: {
          fr: "Quel itinéraire pour 3 semaines au Vietnam ?",
          en: "What itinerary for 3 weeks in Vietnam?",
          es: "¿Qué itinerario para 3 semanas en Vietnam?"
        },
        answer: {
          fr: "Du nord au sud : Hanoi (3j) → Sapa (2j) → Ninh Binh (2j) → Halong (2j) → vol → Hue (2j) → Hoi An (3j) → vol → Ho Chi Minh (2j) → Delta du Mékong (2j).",
          en: "North to south: Hanoi (3d) → Sapa (2d) → Ninh Binh (2d) → Halong (2d) → flight → Hue (2d) → Hoi An (3d) → flight → Ho Chi Minh (2d) → Mekong Delta (2d).",
          es: "De norte a sur: Hanoi (3d) → Sapa (2d) → Ninh Binh (2d) → Halong (2d) → vuelo → Hue (2d) → Hoi An (3d) → vuelo → Ho Chi Minh (2d)."
        }
      },
      {
        question: {
          fr: "Comment se déplacer au Vietnam ?",
          en: "How to get around Vietnam?",
          es: "¿Cómo moverse por Vietnam?"
        },
        answer: {
          fr: "Les vols internes sont bon marché (VietJet, Bamboo). Les bus de nuit (sleeper bus) sont économiques. Le train Reunification relie Hanoi à Saigon en 30h.",
          en: "Domestic flights are cheap (VietJet, Bamboo). Night buses (sleeper bus) are economical. The Reunification train connects Hanoi to Saigon in 30h.",
          es: "Los vuelos internos son baratos (VietJet, Bamboo). Los buses nocturnos (sleeper bus) son económicos."
        }
      },
      {
        question: {
          fr: "Le Vietnam est-il bon marché ?",
          en: "Is Vietnam cheap?",
          es: "¿Es Vietnam barato?"
        },
        answer: {
          fr: "Très ! Comptez 30-50€/jour confortablement. Un repas de rue coûte 1-3€, un hôtel correct 15-30€/nuit. C'est l'une des destinations les moins chères d'Asie.",
          en: "Very! Expect €30-50/day comfortably. Street food costs €1-3, a decent hotel €15-30/night. It's one of Asia's cheapest destinations.",
          es: "¡Muy barato! Cuenta 30-50€/día cómodamente. Una comida callejera cuesta 1-3€, un hotel decente 15-30€/noche."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Téléchargez Grab", en: "Download Grab", es: "Descarga Grab" },
        content: {
          fr: "Grab est l'Uber local, indispensable pour les taxis et scooters-taxis. Prix fixés à l'avance, pas de négociation, paiement en espèces ou carte.",
          en: "Grab is the local Uber, essential for taxis and motorbike-taxis. Prices fixed upfront, no negotiation, cash or card payment.",
          es: "Grab es el Uber local, indispensable para taxis y moto-taxis. Precios fijados de antemano, sin negociación."
        }
      },
      {
        title: { fr: "Le café vietnamien", en: "Vietnamese coffee", es: "El café vietnamita" },
        content: {
          fr: "Ne manquez pas le cà phê sữa đá (café glacé au lait concentré) et le cà phê trứng (café à l'œuf). Une expérience unique !",
          en: "Don't miss cà phê sữa đá (iced coffee with condensed milk) and cà phê trứng (egg coffee). A unique experience!",
          es: "No te pierdas el cà phê sữa đá (café helado con leche condensada) y el cà phê trứng (café con huevo)."
        }
      }
    ]
  },
  "Australie": {
    slug: "australie",
    flag: "🇦🇺",
    description: {
      fr: "L'Australie fascine par ses grands espaces : la Grande Barrière de Corail, l'Outback rouge d'Uluru, les plages de Sydney et la faune unique au monde.",
      en: "Australia fascinates with its vast spaces: the Great Barrier Reef, Uluru's red Outback, Sydney's beaches, and unique wildlife.",
      es: "Australia fascina por sus grandes espacios: la Gran Barrera de Coral, el Outback rojo de Uluru, las playas de Sídney."
    },
    extendedDescription: {
      fr: "L'Australie est un continent à lui seul, offrant des expériences uniques. Des métropoles vibrantes de Sydney et Melbourne aux déserts ocres du Red Centre, des forêts tropicales du Queensland aux côtes sauvages de Tasmanie, le pays abrite aussi une faune extraordinaire : kangourous, koalas, ornithorynques.",
      en: "Australia is a continent unto itself, offering unique experiences. From vibrant Sydney and Melbourne metropolises to the ochre deserts of the Red Centre, from Queensland's tropical forests to Tasmania's wild coasts, the country also hosts extraordinary wildlife: kangaroos, koalas, platypuses.",
      es: "Australia es un continente en sí mismo. Desde las vibrantes metrópolis de Sídney y Melbourne hasta los desiertos ocres del Red Centre."
    },
    keywords: {
      fr: ["voyage australie", "roadtrip australie", "grande barrière de corail", "sydney melbourne"],
      en: ["australia trip", "australia roadtrip", "great barrier reef", "sydney melbourne"],
      es: ["viaje australia", "ruta australia", "gran barrera de coral", "sydney melbourne"]
    },
    highlights: ["Sydney", "Melbourne", "Grande Barrière de Corail", "Uluru", "Tasmanie", "Perth"],
    bestTimeToVisit: {
      fr: "Septembre à Novembre et Mars à Mai",
      en: "September to November and March to May",
      es: "Septiembre a Noviembre y Marzo a Mayo"
    },
    relatedCountries: ["Nouvelle-Zélande", "Indonésie", "Fidji", "Nouvelle-Calédonie"],
    faq: [
      {
        question: {
          fr: "Combien de temps pour visiter l'Australie ?",
          en: "How long to visit Australia?",
          es: "¿Cuánto tiempo para visitar Australia?"
        },
        answer: {
          fr: "Minimum 3 semaines pour la côte Est. 4-6 semaines pour inclure l'Outback ou la côte Ouest. Le pays est immense (14x la France) !",
          en: "Minimum 3 weeks for the East coast. 4-6 weeks to include the Outback or West coast. The country is huge (14x France)!",
          es: "Mínimo 3 semanas para la costa Este. 4-6 semanas para incluir el Outback o la costa Oeste."
        }
      },
      {
        question: {
          fr: "Faut-il un visa pour l'Australie ?",
          en: "Do I need a visa for Australia?",
          es: "¿Necesito visa para Australia?"
        },
        answer: {
          fr: "Oui, un eVisitor (subclass 651) gratuit pour les Européens, ou un ETA (14$) pour d'autres nationalités. Demande en ligne, valable 3 mois.",
          en: "Yes, a free eVisitor (subclass 651) for Europeans, or an ETA ($14) for other nationalities. Apply online, valid for 3 months.",
          es: "Sí, un eVisitor gratuito (subclass 651) para europeos, o un ETA (14$) para otras nacionalidades."
        }
      },
      {
        question: {
          fr: "L'Australie est-elle dangereuse (animaux) ?",
          en: "Is Australia dangerous (animals)?",
          es: "¿Es Australia peligrosa (animales)?"
        },
        answer: {
          fr: "La réputation est exagérée. Respectez les consignes (baignade entre les drapeaux, ne pas marcher dans les hautes herbes) et tout ira bien. Les morsures graves sont rares.",
          en: "The reputation is exaggerated. Follow guidelines (swim between flags, don't walk in tall grass) and you'll be fine. Serious bites are rare.",
          es: "La reputación es exagerada. Sigue las indicaciones (nadar entre banderas, no caminar en hierba alta) y estarás bien."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Le Working Holiday Visa", en: "Working Holiday Visa", es: "Visa de vacaciones y trabajo" },
        content: {
          fr: "Si vous avez moins de 35 ans, le WHV permet de voyager et travailler pendant 1 an (renouvelable). Idéal pour un long séjour auto-financé.",
          en: "If you're under 35, the WHV allows you to travel and work for 1 year (renewable). Ideal for a long, self-funded stay.",
          es: "Si tienes menos de 35 años, el WHV permite viajar y trabajar durante 1 año (renovable)."
        }
      },
      {
        title: { fr: "Attention au soleil", en: "Watch the sun", es: "Cuidado con el sol" },
        content: {
          fr: "L'indice UV en Australie est extrême. Crème solaire SPF50+, chapeau et lunettes sont indispensables, même par temps nuageux.",
          en: "UV index in Australia is extreme. SPF50+ sunscreen, hat, and sunglasses are essential, even on cloudy days.",
          es: "El índice UV en Australia es extremo. Crema solar SPF50+, sombrero y gafas son indispensables."
        }
      }
    ]
  },
  "Nouvelle-Zélande": {
    slug: "nouvelle-zelande",
    flag: "🇳🇿",
    description: {
      fr: "La Nouvelle-Zélande émerveille par ses paysages de cinéma : fjords du Milford Sound, montagnes enneigées, geysers de Rotorua et prairies verdoyantes.",
      en: "New Zealand amazes with its cinematic landscapes: Milford Sound fjords, snow-capped mountains, Rotorua geysers, and lush green pastures.",
      es: "Nueva Zelanda maravilla por sus paisajes de cine: fiordos del Milford Sound, montañas nevadas, géiseres de Rotorua."
    },
    extendedDescription: {
      fr: "La Nouvelle-Zélande est le paradis des amoureux de nature et d'aventure. Terre du Seigneur des Anneaux, elle offre des paysages d'une diversité inouïe sur un petit territoire. Glaciers, fjords, volcans, forêts anciennes et plages sauvages se succèdent, avec une culture maorie vivante et authentique.",
      en: "New Zealand is paradise for nature and adventure lovers. Land of Lord of the Rings, it offers incredibly diverse landscapes in a small territory. Glaciers, fjords, volcanoes, ancient forests, and wild beaches follow one another, with a living and authentic Maori culture.",
      es: "Nueva Zelanda es el paraíso de los amantes de la naturaleza y la aventura. Tierra del Señor de los Anillos, ofrece paisajes de increíble diversidad."
    },
    keywords: {
      fr: ["voyage nouvelle-zélande", "roadtrip nouvelle-zélande", "milford sound", "ile du nord ile du sud"],
      en: ["new zealand trip", "new zealand roadtrip", "milford sound", "north island south island"],
      es: ["viaje nueva zelanda", "ruta nueva zelanda", "milford sound", "isla norte isla sur"]
    },
    highlights: ["Queenstown", "Milford Sound", "Rotorua", "Auckland", "Wellington", "Hobbiton"],
    bestTimeToVisit: {
      fr: "Décembre à Mars (été austral)",
      en: "December to March (austral summer)",
      es: "Diciembre a Marzo (verano austral)"
    },
    relatedCountries: ["Australie", "Fidji", "Tahiti", "Nouvelle-Calédonie"],
    faq: [
      {
        question: {
          fr: "Île du Nord ou île du Sud ?",
          en: "North Island or South Island?",
          es: "¿Isla Norte o Isla Sur?"
        },
        answer: {
          fr: "L'île du Sud pour les paysages grandioses (fjords, glaciers, montagnes). L'île du Nord pour la culture maorie, Rotorua et les plages. Idéalement, les deux !",
          en: "South Island for grandiose landscapes (fjords, glaciers, mountains). North Island for Maori culture, Rotorua, and beaches. Ideally, both!",
          es: "Isla Sur para paisajes grandiosos (fiordos, glaciares). Isla Norte para cultura maorí, Rotorua y playas. ¡Idealmente, ambas!"
        }
      },
      {
        question: {
          fr: "Combien de temps pour un roadtrip en Nouvelle-Zélande ?",
          en: "How long for a New Zealand road trip?",
          es: "¿Cuánto tiempo para un roadtrip en Nueva Zelanda?"
        },
        answer: {
          fr: "Minimum 2 semaines pour une île, 3-4 semaines pour les deux. Ne sous-estimez pas les distances malgré la taille du pays (routes sinueuses).",
          en: "Minimum 2 weeks for one island, 3-4 weeks for both. Don't underestimate distances despite the country's size (winding roads).",
          es: "Mínimo 2 semanas para una isla, 3-4 semanas para ambas. No subestimes las distancias."
        }
      },
      {
        question: {
          fr: "Le van aménagé est-il une bonne option ?",
          en: "Is a campervan a good option?",
          es: "¿Es la furgoneta una buena opción?"
        },
        answer: {
          fr: "Oui, c'est la façon classique de voyager ! Vous économisez l'hébergement et les campings DOC (gratuits ou peu chers) sont partout. Réservez tôt en haute saison.",
          en: "Yes, it's the classic way to travel! You save on accommodation and DOC campgrounds (free or cheap) are everywhere. Book early in high season.",
          es: "Sí, es la forma clásica de viajar. Ahorras alojamiento y los campings DOC (gratuitos o baratos) están por todas partes."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "La NZeTA obligatoire", en: "Mandatory NZeTA", es: "NZeTA obligatoria" },
        content: {
          fr: "Avant de partir, demandez la NZeTA (autorisation de voyage électronique) + taxe environnementale IVL. Comptez 23-47 NZD selon le mode de demande.",
          en: "Before departing, apply for the NZeTA (electronic travel authorization) + IVL environmental tax. Expect 23-47 NZD depending on application method.",
          es: "Antes de partir, solicita la NZeTA (autorización de viaje electrónica) + tasa ambiental IVL. Cuenta 23-47 NZD."
        }
      },
      {
        title: { fr: "Respect de la nature", en: "Respect for nature", es: "Respeto por la naturaleza" },
        content: {
          fr: "Le 'Leave No Trace' est sacré en NZ. Emportez vos déchets, respectez les sentiers, et ne campez que dans les zones autorisées.",
          en: "'Leave No Trace' is sacred in NZ. Take your rubbish, respect trails, and only camp in designated areas.",
          es: "El 'Leave No Trace' es sagrado en NZ. Llévate tu basura, respeta los senderos, y acampa solo en zonas permitidas."
        }
      }
    ]
  },
  "Danemark": {
    slug: "danemark",
    flag: "🇩🇰",
    description: {
      fr: "Le Danemark incarne l'art de vivre scandinave : Copenhague et son design, les châteaux de Kronborg et Frederiksborg, les falaises de Møn et le hygge danois.",
      en: "Denmark embodies Scandinavian art of living: Copenhagen and its design, Kronborg and Frederiksborg castles, Møn cliffs, and Danish hygge.",
      es: "Dinamarca encarna el arte de vivir escandinavo: Copenhague y su diseño, los castillos de Kronborg y Frederiksborg, el hygge danés."
    },
    extendedDescription: {
      fr: "Le Danemark est le pays du bonheur et du design. Copenhague, capitale verte et cyclable, allie architecture moderne et quartiers historiques colorés. Au-delà, découvrez les plages sauvages du Jutland, les villages de pêcheurs pittoresques et une gastronomie nordique étoilée.",
      en: "Denmark is the country of happiness and design. Copenhagen, a green, bike-friendly capital, combines modern architecture with colorful historic quarters. Beyond, discover Jutland's wild beaches, picturesque fishing villages, and starred Nordic gastronomy.",
      es: "Dinamarca es el país de la felicidad y el diseño. Copenhague combina arquitectura moderna con barrios históricos coloridos."
    },
    keywords: {
      fr: ["voyage danemark", "copenhague", "hygge", "scandinavie"],
      en: ["denmark trip", "copenhagen", "hygge", "scandinavia"],
      es: ["viaje dinamarca", "copenhague", "hygge", "escandinavia"]
    },
    highlights: ["Copenhague", "Kronborg", "Aarhus", "Skagen", "Îles Féroé", "Legoland"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Suède", "Norvège", "Allemagne", "Pays-Bas"],
    faq: [
      {
        question: {
          fr: "Combien de jours pour visiter Copenhague ?",
          en: "How many days to visit Copenhagen?",
          es: "¿Cuántos días para visitar Copenhague?"
        },
        answer: {
          fr: "3-4 jours suffisent pour les incontournables : Nyhavn, Tivoli, Christiania, le quartier de Vesterbro, et une excursion à Kronborg ou Louisiane.",
          en: "3-4 days are enough for the essentials: Nyhavn, Tivoli, Christiania, Vesterbro district, and a trip to Kronborg or Louisiana museum.",
          es: "3-4 días bastan para lo esencial: Nyhavn, Tivoli, Christiania, el barrio de Vesterbro, y una excursión a Kronborg."
        }
      },
      {
        question: {
          fr: "Le Danemark est-il cher ?",
          en: "Is Denmark expensive?",
          es: "¿Es Dinamarca caro?"
        },
        answer: {
          fr: "Oui, c'est l'un des pays les plus chers d'Europe. Comptez 150-200€/jour. Les musées sont souvent gratuits le mercredi. Achetez à emporter pour économiser.",
          en: "Yes, it's one of Europe's most expensive countries. Expect €150-200/day. Museums are often free on Wednesdays. Get takeaway to save money.",
          es: "Sí, es uno de los países más caros de Europa. Cuenta 150-200€/día. Los museos suelen ser gratis los miércoles."
        }
      },
      {
        question: {
          fr: "Comment se déplacer au Danemark ?",
          en: "How to get around Denmark?",
          es: "¿Cómo moverse por Dinamarca?"
        },
        answer: {
          fr: "À vélo ! C'est le pays le plus cyclable au monde. Sinon, les trains et bus sont excellents. La Copenhagen Card inclut transports et attractions.",
          en: "By bike! It's the world's most bike-friendly country. Otherwise, trains and buses are excellent. The Copenhagen Card includes transport and attractions.",
          es: "¡En bici! Es el país más ciclista del mundo. Si no, trenes y buses son excelentes. La Copenhagen Card incluye transporte y atracciones."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Le hygge danois", en: "Danish hygge", es: "El hygge danés" },
        content: {
          fr: "Le hygge est l'art du cocooning à la danoise. Profitez des cafés chaleureux avec bougies, couvertures et pâtisseries. C'est l'essence du bonheur danois !",
          en: "Hygge is the Danish art of coziness. Enjoy warm cafés with candles, blankets, and pastries. It's the essence of Danish happiness!",
          es: "El hygge es el arte danés de la comodidad. Disfruta de cafés acogedores con velas, mantas y pasteles."
        }
      },
      {
        title: { fr: "Street food à Copenhague", en: "Copenhagen street food", es: "Street food en Copenhague" },
        content: {
          fr: "Reffen (Refshaleøen) et Torvehallerne sont parfaits pour manger varié et moins cher. Smørrebrød, hot-dogs, et cuisine du monde.",
          en: "Reffen (Refshaleøen) and Torvehallerne are perfect for varied, cheaper eating. Smørrebrød, hot dogs, and world cuisine.",
          es: "Reffen (Refshaleøen) y Torvehallerne son perfectos para comer variado y más barato."
        }
      }
    ]
  },
  "Costa Rica": {
    slug: "costa-rica",
    flag: "🇨🇷",
    description: {
      fr: "Le Costa Rica enchante par sa nature luxuriante : volcans actifs, forêts tropicales peuplées de paresseux et toucans, plages des Caraïbes et du Pacifique.",
      en: "Costa Rica enchants with its lush nature: active volcanoes, tropical forests with sloths and toucans, Caribbean and Pacific beaches.",
      es: "Costa Rica encanta por su naturaleza exuberante: volcanes activos, bosques tropicales con perezosos y tucanes."
    },
    extendedDescription: {
      fr: "Le Costa Rica est un pionnier de l'écotourisme. Ce petit pays d'Amérique centrale abrite 5% de la biodiversité mondiale. Des canopées de Monteverde aux plages de Manuel Antonio, des sources chaudes d'Arenal aux tortues de Tortuguero, la faune et la flore sont omniprésentes.",
      en: "Costa Rica is an ecotourism pioneer. This small Central American country hosts 5% of the world's biodiversity. From Monteverde canopies to Manuel Antonio beaches, from Arenal hot springs to Tortuguero turtles, wildlife is everywhere.",
      es: "Costa Rica es pionero del ecoturismo. Este pequeño país de América Central alberga el 5% de la biodiversidad mundial."
    },
    keywords: {
      fr: ["voyage costa rica", "costa rica nature", "arenal volcan", "manuel antonio"],
      en: ["costa rica trip", "costa rica nature", "arenal volcano", "manuel antonio"],
      es: ["viaje costa rica", "costa rica naturaleza", "volcán arenal", "manuel antonio"]
    },
    highlights: ["Arenal", "Monteverde", "Manuel Antonio", "Tortuguero", "Corcovado", "Playa Tamarindo"],
    bestTimeToVisit: {
      fr: "Décembre à Avril (saison sèche)",
      en: "December to April (dry season)",
      es: "Diciembre a Abril (estación seca)"
    },
    relatedCountries: ["Panama", "Nicaragua", "Mexique", "Guatemala"],
    faq: [
      {
        question: {
          fr: "Quel itinéraire pour 2 semaines au Costa Rica ?",
          en: "What itinerary for 2 weeks in Costa Rica?",
          es: "¿Qué itinerario para 2 semanas en Costa Rica?"
        },
        answer: {
          fr: "San José → Arenal (3j) → Monteverde (2j) → Manuel Antonio (3j) → Puerto Viejo Caraïbes (3j). Ou remplacez Puerto Viejo par Corcovado pour plus de nature sauvage.",
          en: "San José → Arenal (3d) → Monteverde (2d) → Manuel Antonio (3d) → Puerto Viejo Caribbean (3d). Or swap Puerto Viejo for Corcovado for more wildlife.",
          es: "San José → Arenal (3d) → Monteverde (2d) → Manuel Antonio (3d) → Puerto Viejo Caribe (3d)."
        }
      },
      {
        question: {
          fr: "Faut-il louer un 4x4 au Costa Rica ?",
          en: "Should I rent a 4x4 in Costa Rica?",
          es: "¿Debo alquilar un 4x4 en Costa Rica?"
        },
        answer: {
          fr: "Recommandé, surtout en saison des pluies. Certaines routes sont non goudronnées et les rivières à traverser. Les agences locales sont souvent moins chères.",
          en: "Recommended, especially in rainy season. Some roads are unpaved and rivers need crossing. Local agencies are often cheaper.",
          es: "Recomendado, especialmente en temporada de lluvias. Algunas carreteras no están pavimentadas."
        }
      },
      {
        question: {
          fr: "Le Costa Rica est-il cher ?",
          en: "Is Costa Rica expensive?",
          es: "¿Es Costa Rica caro?"
        },
        answer: {
          fr: "Plus cher que ses voisins d'Amérique centrale. Comptez 80-120€/jour. Les lodges eco-luxe peuvent être très chers. Les 'sodas' (cantines locales) sont économiques.",
          en: "More expensive than its Central American neighbors. Expect €80-120/day. Eco-luxury lodges can be very pricey. Local 'sodas' (canteens) are budget-friendly.",
          es: "Más caro que sus vecinos centroamericanos. Cuenta 80-120€/día. Las 'sodas' locales son económicas."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Pura Vida !", en: "Pura Vida!", es: "¡Pura Vida!" },
        content: {
          fr: "L'expression nationale signifie 'la vie pure'. C'est une philosophie de vie positive et détendue. Adoptez-la pendant votre séjour !",
          en: "The national expression means 'pure life'. It's a positive, relaxed life philosophy. Adopt it during your stay!",
          es: "La expresión nacional significa 'vida pura'. Es una filosofía de vida positiva y relajada. ¡Adóptala durante tu estancia!"
        }
      },
      {
        title: { fr: "Observation de la faune", en: "Wildlife watching", es: "Observación de fauna" },
        content: {
          fr: "Engagez un guide naturaliste local. Ils repèrent les animaux invisibles aux non-initiés : paresseux, grenouilles colorées, serpents, singes...",
          en: "Hire a local naturalist guide. They spot animals invisible to the untrained eye: sloths, colorful frogs, snakes, monkeys...",
          es: "Contrata un guía naturalista local. Detectan animales invisibles para los no iniciados: perezosos, ranas, monos..."
        }
      }
    ]
  },
  "Finlande": {
    slug: "finlande",
    flag: "🇫🇮",
    description: {
      fr: "La Finlande enchante par ses paysages nordiques : aurores boréales en Laponie, lacs cristallins, forêts infinies et l'authentique village du Père Noël à Rovaniemi.",
      en: "Finland enchants with its Nordic landscapes: Lapland's northern lights, crystal lakes, endless forests, and Santa's authentic village in Rovaniemi.",
      es: "Finlandia encanta por sus paisajes nórdicos: auroras boreales en Laponia, lagos cristalinos, bosques infinitos."
    },
    extendedDescription: {
      fr: "La Finlande est le pays du sauna, des mille lacs et du bonheur. En été, le soleil de minuit illumine des paysages de lacs et forêts. En hiver, la Laponie offre aurores boréales, traîneaux à chiens et igloos de verre. Helsinki, design et nature, complète le tableau.",
      en: "Finland is the land of sauna, a thousand lakes, and happiness. In summer, the midnight sun illuminates lake and forest landscapes. In winter, Lapland offers northern lights, dog sledding, and glass igloos. Helsinki, design and nature, completes the picture.",
      es: "Finlandia es el país del sauna, los mil lagos y la felicidad. En verano, el sol de medianoche ilumina paisajes de lagos y bosques."
    },
    keywords: {
      fr: ["voyage finlande", "laponie finlande", "aurores boréales finlande", "helsinki"],
      en: ["finland trip", "lapland finland", "northern lights finland", "helsinki"],
      es: ["viaje finlandia", "laponia finlandia", "auroras boreales finlandia", "helsinki"]
    },
    highlights: ["Helsinki", "Rovaniemi", "Laponie", "Lacs finlandais", "Tampere", "Archipel de Turku"],
    bestTimeToVisit: {
      fr: "Décembre à Mars (aurores) ou Juin à Août (été)",
      en: "December to March (auroras) or June to August (summer)",
      es: "Diciembre a Marzo (auroras) o Junio a Agosto (verano)"
    },
    relatedCountries: ["Suède", "Norvège", "Estonie", "Islande"],
    faq: [
      {
        question: {
          fr: "Quand voir les aurores boréales en Finlande ?",
          en: "When to see northern lights in Finland?",
          es: "¿Cuándo ver auroras boreales en Finlandia?"
        },
        answer: {
          fr: "De septembre à mars en Laponie, au-dessus du cercle polaire. Les mois de février-mars offrent le meilleur équilibre entre nuits longues et météo clémente.",
          en: "From September to March in Lapland, above the Arctic Circle. February-March offers the best balance of long nights and decent weather.",
          es: "De septiembre a marzo en Laponia, por encima del círculo polar. Febrero-marzo ofrece el mejor equilibrio."
        }
      },
      {
        question: {
          fr: "La Finlande est-elle chère ?",
          en: "Is Finland expensive?",
          es: "¿Es Finlandia cara?"
        },
        answer: {
          fr: "Oui, c'est l'un des pays les plus chers d'Europe. Comptez 120-180€/jour. Les activités en Laponie (chiens de traîneau, motoneige) sont coûteuses.",
          en: "Yes, it's one of Europe's most expensive countries. Expect €120-180/day. Lapland activities (dog sledding, snowmobiling) are costly.",
          es: "Sí, es uno de los países más caros de Europa. Cuenta 120-180€/día."
        }
      },
      {
        question: {
          fr: "Que faire en Finlande en été ?",
          en: "What to do in Finland in summer?",
          es: "¿Qué hacer en Finlandia en verano?"
        },
        answer: {
          fr: "Profitez du soleil de minuit ! Randonnées dans les parcs nationaux, kayak sur les lacs, cueillette de baies, séjour dans un chalet au bord de l'eau avec sauna.",
          en: "Enjoy the midnight sun! Hiking in national parks, kayaking on lakes, berry picking, staying in a lakeside cottage with sauna.",
          es: "¡Disfruta del sol de medianoche! Senderismo, kayak en los lagos, recolección de bayas, estancia en cabaña con sauna."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "Le sauna finlandais", en: "Finnish sauna", es: "La sauna finlandesa" },
        content: {
          fr: "Le sauna est une institution en Finlande (3 millions de saunas pour 5,5 millions d'habitants !). Testez un sauna public ou louez un chalet avec sauna privé.",
          en: "Sauna is an institution in Finland (3 million saunas for 5.5 million people!). Try a public sauna or rent a cottage with private sauna.",
          es: "La sauna es una institución en Finlandia. Prueba una sauna pública o alquila una cabaña con sauna privada."
        }
      },
      {
        title: { fr: "Le droit d'accès à la nature", en: "Everyman's right", es: "Derecho de acceso a la naturaleza" },
        content: {
          fr: "En Finlande, vous pouvez camper et cueillir des baies partout dans la nature (sauf propriétés privées proches des habitations). Un droit ancestral précieux !",
          en: "In Finland, you can camp and pick berries anywhere in nature (except private properties near homes). A precious ancestral right!",
          es: "En Finlandia, puedes acampar y recoger bayas en cualquier lugar de la naturaleza. ¡Un derecho ancestral!"
        }
      }
    ]
  },
  "Monténégro": {
    slug: "montenegro",
    flag: "🇲🇪",
    description: {
      fr: "Le Monténégro surprend par sa beauté compacte : les bouches de Kotor, les plages de Budva, les montagnes du Durmitor et les villages perchés de l'arrière-pays.",
      en: "Montenegro surprises with its compact beauty: the Bay of Kotor, Budva's beaches, Durmitor mountains, and perched villages of the hinterland.",
      es: "Montenegro sorprende por su belleza compacta: la bahía de Kotor, las playas de Budva, las montañas del Durmitor."
    },
    extendedDescription: {
      fr: "Le Monténégro est un joyau des Balkans souvent méconnu. Sur un petit territoire, il concentre des fjords méditerranéens spectaculaires, des plages de sable, des canyons vertigineux et des parcs nationaux montagneux. Kotor, classée UNESCO, rivalise de beauté avec Dubrovnik, pour une fraction du prix.",
      en: "Montenegro is an often-overlooked Balkan gem. In a small territory, it packs spectacular Mediterranean fjords, sandy beaches, vertiginous canyons, and mountainous national parks. UNESCO-listed Kotor rivals Dubrovnik in beauty, at a fraction of the price.",
      es: "Montenegro es una joya de los Balcanes a menudo desconocida. En un pequeño territorio, concentra fiordos mediterráneos espectaculares, playas y parques nacionales."
    },
    keywords: {
      fr: ["voyage monténégro", "kotor", "budva", "durmitor"],
      en: ["montenegro trip", "kotor", "budva", "durmitor"],
      es: ["viaje montenegro", "kotor", "budva", "durmitor"]
    },
    highlights: ["Kotor", "Budva", "Sveti Stefan", "Durmitor", "Lac de Skadar", "Perast"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Croatie", "Albanie", "Bosnie-Herzégovine", "Serbie"],
    faq: [
      {
        question: {
          fr: "Combien de jours pour visiter le Monténégro ?",
          en: "How many days to visit Montenegro?",
          es: "¿Cuántos días para visitar Montenegro?"
        },
        answer: {
          fr: "5-7 jours suffisent pour les incontournables : Kotor/Perast (2j), Budva/Sveti Stefan (2j), Durmitor ou lac Skadar (2j). Le pays est petit et facile à parcourir.",
          en: "5-7 days are enough for the essentials: Kotor/Perast (2d), Budva/Sveti Stefan (2d), Durmitor or Skadar Lake (2d). The country is small and easy to explore.",
          es: "5-7 días bastan para lo esencial: Kotor/Perast (2d), Budva/Sveti Stefan (2d), Durmitor o lago Skadar (2d)."
        }
      },
      {
        question: {
          fr: "Le Monténégro est-il bon marché ?",
          en: "Is Montenegro cheap?",
          es: "¿Es Montenegro barato?"
        },
        answer: {
          fr: "Globalement oui, moins cher que la Croatie voisine. Comptez 50-80€/jour. La côte est plus chère que l'intérieur. Budva et Sveti Stefan sont les plus chers.",
          en: "Generally yes, cheaper than neighboring Croatia. Expect €50-80/day. The coast is pricier than the interior. Budva and Sveti Stefan are most expensive.",
          es: "En general sí, más barato que Croacia. Cuenta 50-80€/día. La costa es más cara que el interior."
        }
      },
      {
        question: {
          fr: "Quelle monnaie au Monténégro ?",
          en: "What currency in Montenegro?",
          es: "¿Qué moneda en Montenegro?"
        },
        answer: {
          fr: "L'euro, bien que le Monténégro ne soit pas dans l'UE. Les cartes sont largement acceptées, mais gardez du liquide pour les petits commerces et l'intérieur du pays.",
          en: "The euro, although Montenegro isn't in the EU. Cards are widely accepted, but keep cash for small shops and the interior.",
          es: "El euro, aunque Montenegro no está en la UE. Las tarjetas son ampliamente aceptadas."
        }
      },
      {
        question: {
          fr: "Comment se rendre au Monténégro ?",
          en: "How to get to Montenegro?",
          es: "¿Cómo llegar a Montenegro?"
        },
        answer: {
          fr: "Vols vers Podgorica ou Tivat (plus proche de Kotor). Alternative : voler vers Dubrovnik (Croatie) et traverser la frontière en 1h30.",
          en: "Flights to Podgorica or Tivat (closer to Kotor). Alternative: fly to Dubrovnik (Croatia) and cross the border in 1.5h.",
          es: "Vuelos a Podgorica o Tivat. Alternativa: volar a Dubrovnik y cruzar la frontera en 1h30."
        }
      }
    ],
    travelTips: [
      {
        title: { fr: "La route serpentine de Kotor", en: "Kotor's serpentine road", es: "La carretera serpentina de Kotor" },
        content: {
          fr: "Les 25 virages en épingle au-dessus de Kotor offrent des vues spectaculaires. Montez en voiture ou à pied jusqu'au fort de Lovcen pour des panoramas inoubliables.",
          en: "The 25 hairpin turns above Kotor offer spectacular views. Drive or hike up to Lovcen fortress for unforgettable panoramas.",
          es: "Las 25 curvas cerradas sobre Kotor ofrecen vistas espectaculares. Sube en coche o a pie hasta el fuerte de Lovcen."
        }
      },
      {
        title: { fr: "Évitez juillet-août sur la côte", en: "Avoid July-August on the coast", es: "Evita julio-agosto en la costa" },
        content: {
          fr: "Les plages de Budva sont bondées en plein été. Préférez juin ou septembre pour profiter du beau temps sans la foule, et des prix plus doux.",
          en: "Budva beaches are crowded in peak summer. Prefer June or September to enjoy good weather without crowds, and softer prices.",
          es: "Las playas de Budva están abarrotadas en pleno verano. Prefiere junio o septiembre."
        }
      }
    ]
  },
  // Merge new countries
  ...newCountryContent,
  // Merge additional countries (batch 2)
  ...additionalCountryContent
};

// Helper functions
export const getCountryContent = (countryName: string): CountryContent | null => {
  return countryContent[countryName] || null;
};

export const getCountryBySlug = (slug: string): { name: string; content: CountryContent } | null => {
  for (const [name, content] of Object.entries(countryContent)) {
    if (content.slug === slug) {
      return { name, content };
    }
  }
  return null;
};

export const getAllCountrySlugs = (): string[] => {
  return Object.values(countryContent).map(c => c.slug);
};

export const countryNameToSlug = (name: string): string => {
  const content = countryContent[name];
  if (content) return content.slug;
  
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
};

export const slugToCountryName = (slug: string): string | null => {
  const entry = getCountryBySlug(slug);
  if (entry) return entry.name;
  return null;
};

export const getDefaultCountryContent = (countryName: string, lang: string): { description: string; keywords: string[] } => {
  const descriptions = {
    fr: `Découvrez nos itinéraires de voyage en ${countryName}. Explorez les meilleures destinations partagées par notre communauté.`,
    en: `Discover our travel itineraries in ${countryName}. Explore the best destinations shared by our traveler community.`,
    es: `Descubre nuestros itinerarios de viaje en ${countryName}. Explora los mejores destinos compartidos por nuestra comunidad.`
  };
  
  const keywords = {
    fr: [`voyage ${countryName.toLowerCase()}`, `itinéraire ${countryName.toLowerCase()}`],
    en: [`${countryName.toLowerCase()} trip`, `${countryName.toLowerCase()} itinerary`],
    es: [`viaje ${countryName.toLowerCase()}`, `itinerario ${countryName.toLowerCase()}`]
  };
  
  return {
    description: descriptions[lang as keyof typeof descriptions] || descriptions.fr,
    keywords: keywords[lang as keyof typeof keywords] || keywords.fr
  };
};
